/**
* DevExpress Dashboard (_default.js)
* Version:  24.1.7
* Build date: Oct 29, 2024
* Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
* License: https://www.devexpress.com/Support/EULAs/universal.xml
*/
import { localize } from '@devexpress/analytics-core/analytics-internal';
import { format } from 'devextreme/core/utils/string';
export const defaultLocalization = {
    'DashboardStringId.ActionClearMasterFilter': 'Clear Master Filter',
    'DashboardStringId.ActionClearSelection': 'Clear Selection',
    'DashboardStringId.ActionOtherValues': 'Values',
    'DashboardStringId.ActionDrillUp': 'Drill Up',
    'DashboardStringId.ActionExportTo': 'Export To',
    'DashboardStringId.ActionExportToPdf': 'Export To PDF',
    'DashboardStringId.ActionExportToImage': 'Export To Image',
    'DashboardStringId.ActionExportToExcel': 'Export To Excel',
    'DashboardStringId.RangeFilterCustomDateTimePeriod': 'Custom Period',
    'DashboardStringId.ActionExportTemplate': '{0} - {1}',
    'DashboardStringId.ActionAllowMultiselection': 'Multi-Select',
    'DashboardStringId.DashboardNullValue': ' ',
    'DashboardStringId.TopNOthersValue': 'Others',
    'DashboardStringId.DashboardErrorValue': 'Error',
    'DashboardStringId.DateTimeQuarterFormat': 'Q{0}',
    'DashboardStringId.MessageLoading': 'Loading...',
    'DashboardStringId.ParametersFormCaption': 'Dashboard Parameters',
    'DashboardStringId.ParametersSelectorText': 'Select...',
    'DashboardStringId.ParametersFormAllowNullColumnCaption': 'Pass Null',
    'DashboardStringId.ParametersFormValueColumnCaption': 'Value',
    'DashboardStringId.ParametersFormNameColumnCaption': 'Parameter Name',
    'DashboardStringId.ButtonOK': 'OK',
    'DashboardStringId.ButtonCancel': 'Cancel',
    'DashboardStringId.SelectDate': 'Select date',
    'DashboardStringId.ButtonReset': 'Reset',
    'DashboardStringId.ButtonSubmit': 'Submit',
    'DashboardStringId.ButtonApply': 'Apply',
    'DashboardStringId.ButtonExport': 'Export',
    'DashboardStringId.GridResetColumnWidths': 'Reset column widths',
    'DashboardStringId.GridSortAscending': 'Sort Ascending',
    'DashboardStringId.GridSortDescending': 'Sort Descending',
    'DashboardStringId.GridClearSorting': 'Clear Sorting',
    'DashboardStringId.PivotGridTotal': '{0} Total',
    'DashboardStringId.PivotGridGrandTotal': 'Grand Total',
    'DashboardStringId.ChartTotalValue': 'Total',
    'DashboardStringId.PageLayout': 'Page Layout',
    'DashboardStringId.PageLayoutAuto': 'Auto',
    'DashboardStringId.PageLayoutPortrait': 'Portrait',
    'DashboardStringId.PageLayoutLandscape': 'Landscape',
    'DashboardStringId.PaperKind': 'Size',
    'DashboardStringId.PaperKindLetter': 'Letter',
    'DashboardStringId.PaperKindLegal': 'Legal',
    'DashboardStringId.PaperKindExecutive': 'Executive',
    'DashboardStringId.PaperKindA5': 'A5',
    'DashboardStringId.PaperKindA4': 'A4',
    'DashboardStringId.PaperKindA3': 'A3',
    'DashboardStringId.ScaleMode': 'Scale Mode',
    'DashboardStringId.ScaleModeNone': 'None',
    'DashboardStringId.ScaleModeUseScaleFactor': 'Use Scale Factor',
    'DashboardStringId.ScaleModeAutoFitToPagesWidth': 'Auto Fit to Pages Width',
    'DashboardStringId.AutoFitPageCount': 'Auto Fit Page Count',
    'DashboardStringId.ScaleFactor': 'Scale Factor',
    'DashboardStringId.PrintHeadersOnEveryPage': 'Print Headers on Every Page',
    'DashboardStringId.FitToPageWidth': 'Fit to Page Width',
    'DashboardStringId.SizeMode': 'Size Mode',
    'DashboardStringId.SizeModeNone': 'None',
    'DashboardStringId.SizeModeStretch': 'Stretch',
    'DashboardStringId.SizeModeZoom': 'Zoom',
    'DashboardStringId.AutoArrangeContent': 'Auto Arrange Content',
    'DashboardStringId.ImageFormat': 'Image Format',
    'DashboardStringId.ExcelFormat': 'ExcelFormat',
    'DashboardStringId.CsvValueSeparator': 'Separator',
    'DashboardStringId.ExportFilters': 'Export Filters',
    'DashboardStringId.ExportParameters': 'Export Parameters',
    'DashboardStringId.DashboardStatePosition': 'Dashboard State',
    'DashboardStringId.Resolution': 'Resolution (dpi)',
    'DashboardStringId.ShowTitle': 'Show Title',
    'DashboardStringId.Title': 'Title',
    'DashboardStringId.FileName': 'File Name',
    'DashboardStringId.FilterStatePresentationAfter': 'Below',
    'DashboardStringId.FilterStatePresentationAfterAndSplitPage': 'Separate Page',
    'DashboardStringId.FilterStatePresentationSplitSheet': 'Separate Sheet',
    'DashboardStringId.MessageCustomItemIsNotSupported': 'The custom item is not supported by this control.',
    'DashboardStringId.MessageGridHasNoData': 'The grid has no data.',
    'DashboardStringId.MessagePivotHasNoData': 'The pivot grid has no data.',
    'DashboardStringId.SparklineTooltipStartValue': 'Start:',
    'DashboardStringId.SparklineTooltipEndValue': 'End:',
    'DashboardStringId.SparklineTooltipMinValue': 'Min:',
    'DashboardStringId.SparklineTooltipMaxValue': 'Max:',
    'DashboardStringId.OpenCaption': 'Open',
    'DashboardStringId.HighCaption': 'High',
    'DashboardStringId.LowCaption': 'Low',
    'DashboardStringId.CloseCaption': 'Close',
    'DashboardStringId.InitialExtent': 'Initial Extent',
    'DashboardStringId.FilterElementShowAllItem': '(All)',
    'DashboardStringId.NumericFormatUnitSymbolThousands': 'K',
    'DashboardStringId.NumericFormatUnitSymbolMillions': 'M',
    'DashboardStringId.NumericFormatUnitSymbolBillions': 'B',
    'DashboardStringId.ActionSelectDateTimePeriod': 'Select Date Time Period',
    'DashboardStringId.RangeFilterSelectRange': 'Select Range',
    'DashboardStringId.FilterElementCheckedComboBoxNoDataCaption': '(none)',
    'DashboardStringId.FilterElementRadioComboBoxNoDataCaption': 'Select...',
    'DashboardStringId.FilterElementNoDataToDisplay': 'No data to display',
    'DashboardWebStringId.FilterElementCheckedComboBoxSelected': '{0} selected',
    'DashboardStringId.RangeFilterSelectRangeFromCaption': 'From:',
    'DashboardStringId.RangeFilterSelectRangeToCaption': 'To:',
    'DashboardWebStringId.DashboardParameters': 'Parameters',
    'DashboardStringId.DataSourceDataMember': 'Data Member',
    'DashboardStringId.DescriptionMeasures': 'Measures',
    'DashboardWebStringId.TextBoxFormatText': 'Format',
    'DashboardStringId.NumericFormatUnitCaption': 'Unit',
    'DashboardWebStringId.SummaryType': 'Summary Type',
    'DashboardStringId.CalculationTypeExpression': 'Expression',
    'DevExpressWebStringId.CalculatedFieldDataType': 'DataType',
    'DashboardStringId.ValueCaption': 'Value',
    'DashboardStringId.Value1Caption': 'Value 1',
    'DashboardStringId.Value2Caption': 'Value 2',
    'DashboardStringId.CommandFormatRuleTopBottom': 'Top/Bottom',
    'DashboardStringId.CommandFormatRuleAboveBelowAverage': 'Average',
    'DashboardStringId.CommandFormatRuleBar': 'Bar',
    'DashboardStringId.CommandFormatRuleColorRangeBar': 'Color Range Bar',
    'DashboardStringId.CommandFormatRuleGradientRangeBar': 'Bar Gradient Ranges',
    'DashboardStringId.FormatRuleStyleAppearance': 'Appearance',
    'DashboardStringId.FormatRuleStyleIcons': 'Icons',
    'DashboardStringId.FormatRuleNegativeStyle': 'NegativeStyleSettings',
    'DashboardStringId.CommandFormatRuleExpression': 'Expression',
    'DashboardWebStringId.TopNMode': 'Mode',
    'DashboardStringId.CommandFormatRuleRangeSet': 'Range Set',
    'DashboardStringId.CommandFormatRuleRangeSetRanges2': 'Ranges 2',
    'DashboardStringId.CommandFormatRuleRangeSetRanges3': 'Ranges 3',
    'DashboardStringId.CommandFormatRuleRangeSetRanges4': 'Ranges 4',
    'DashboardStringId.CommandFormatRuleRangeSetRanges5': 'Ranges 5',
    'DashboardStringId.CommandFormatRuleRangeSet2ColorGradientRanges': '2 Color Gradient Ranges',
    'DashboardStringId.CommandFormatRuleRangeSet3ColorGradientRanges': '3 Color Gradient Ranges',
    'DashboardStringId.CommandFormatRuleValue': 'Value',
    'DashboardStringId.CommandFormatRuleDateOccurring': 'A Date Occurring',
    'DashboardWebStringId.ConditionalFormatting.Appearance.BackColor': 'Background color',
    'DashboardWebStringId.ConditionalFormatting.Appearance.ForeColor': 'Text color',
    'DashboardStringId.DescriptionBoundImageAttribute': 'Attribute',
    'DashboardWebStringId.ShowCaptionText': 'Show Caption',
    'DashboardWebStringId.DisplayItemAsPage': 'Display Item as Page',
    'DashboardStringId.DefaultNameItemGroup': 'Group',
    'DashboardStringId.DefaultNameTabContainerItem': 'Tab Container',
    'DashboardStringId.DefaultDataSourceName': 'Data Source',
    'DashboardWebStringId.CrossDataSourceFiltering': 'Cross-Data-Source Filtering',
    'DashboardStringId.RangeFilterCalculationAlongSeries': 'Series',
    'DashboardStringId.RangeFilterCalculationAlongArgument': 'Argument',
    'DashboardStringId.DescriptionItemSeries': 'Series',
    'DashboardStringId.DescriptionSeries': 'Series',
    'DashboardWebStringId.Chart.SeriesType': 'Series Type',
    'DashboardStringId.DescriptionDimensions': 'Dimensions',
    'DashboardWebStringId.TreeView.AutoExpand': 'AutoExpand',
    'DashboardWebStringId.ConditionalFormatting.ConditionMinimum': 'Minimum',
    'DashboardWebStringId.ConditionalFormatting.ConditionMaximum': 'Maximum',
    'DashboardStringId.DescriptionItemDimension': 'Dimension',
    'DashboardStringId.DescriptionItemMeasure': 'Measure',
    'DashboardWebStringId.Grid.MergeCells': 'Merge Cells',
    'DashboardWebStringId.Grid.ColumnWidthMode': 'Column Width Mode',
    'DashboardWebStringId.Grid.BandedRows': 'Banded Rows',
    'DashboardWebStringId.Grid.VerticalLines': 'Vertical Lines',
    'DashboardWebStringId.Grid.HorizontalLines': 'Horizontal Lines',
    'DashboardWebStringId.Grid.ColumnHeaders': 'Column Headers',
    'DashboardWebStringId.Grid.WordWrap': 'Word Wrap',
    'DashboardWebStringId.Grid.ShowFilterRow': 'Filter Row',
    'DashboardWebStringId.Grid.UpdateTotals': 'Update Totals',
    'DashboardWebStringId.Grid.GridColumnFilter': 'Column Filter',
    'DashboardStringId.ActualValueCaption': 'Actual',
    'DashboardStringId.TargetValueCaption': 'Target',
    'DashboardStringId.UriPatternCaption': 'Uri Pattern',
    'DashboardStringId.UriCaption': 'Uri',
    'DashboardStringId.DisplayValueCaption': 'Display Value',
    'DashboardStringId.WeightCaption': 'Weight',
    'DashboardStringId.DescriptionItemColor': 'Color',
    'DashboardStringId.Latitude': 'Latitude',
    'DashboardStringId.Longitude': 'Longitude',
    'DashboardWebStringId.GeoPointMapClusterization': 'Enable Clustering',
    'DashboardWebStringId.MapLockNavigation': 'Navigation',
    'DashboardWebStringId.WeightedLegendGalleryGroup': 'Position',
    'DashboardStringId.DescriptionItemArgument': 'Argument',
    'DashboardWebStringId.PieMapIsWeighted': 'Weighted Pies',
    'DashboardWebStringId.InteractivityOptions.IgnoreMasterFilters': 'Ignore Master Filters',
    'DashboardWebStringId.InteractivityOptions.DrillDown': 'Drill Down',
    'DashboardWebStringId.Pie.ShowCaptions': 'Show Pie Captions',
    'DashboardStringId.DescriptionColumns': 'Columns',
    'DashboardStringId.DescriptionRows': 'Rows',
    'DashboardStringId.DescriptionValues': 'Values',
    'DashboardStringId.DescriptionArguments': 'Arguments',
    'DashboardStringId.TextGroupIntervalAlphabetical': 'Alphabetical',
    'DashboardStringId.ActionMaximizeDashboardItem': 'Maximize',
    'DashboardStringId.ActionRestoreDashboardItem': 'Restore Down',
    'DevExpress.DashboardCommon.DynamicListLookUpSettings.DataSource': 'Data Source',
    'DevExpress.DashboardCommon.DynamicListLookUpSettings.DataMember': 'Data Member',
    'DevExpress.DashboardCommon.DynamicListLookUpSettings.ValueMember': 'Value Member',
    'DevExpress.DashboardCommon.DynamicListLookUpSettings.DisplayMember': 'Display Member',
    'DevExpress.DashboardCommon.DynamicListLookUpSettings.SortOrder': 'Sort Order',
    'DevExpress.DashboardCommon.DashboardParameter.Visible': 'Visible',
    'DevExpress.DashboardCommon.DashboardParameter.Description': 'Description',
    'DevExpress.DashboardCommon.DashboardParameter.AllowMultiselect': 'Allow Multiselect',
    'DevExpress.DashboardCommon.DashboardParameter.AllowNull': 'Allow Null',
    'DevExpress.DashboardCommon.DashboardParameter.SelectAllValues': 'Select All Values',
    'DevExpress.DashboardCommon.StaticListLookUpSettings.Values': 'Values',
    'DevExpress.DashboardCommon.DimensionSortOrder.Ascending': 'Ascending',
    'DevExpress.DashboardCommon.DimensionSortOrder.Descending': 'Descending',
    'DevExpress.DashboardCommon.DimensionSortOrder.None': 'None',
    'DashboardWebStringId.AccordionTab.ScatterChartPointLabelOptions': 'Point Labels',
    'DashboardWebStringId.AccordionTab.ChoroplethMapShapeLabelsAttribute': 'Shape Labels',
    'DashboardStringId.CommandFormatRuleTopN': 'Top N',
    'DashboardWebStringId.Type': 'Type',
    'DashboardWebStringId.Grid.ColumnWidth': 'Column Width',
    'DashboardWebStringId.AccordionTab.ShowTotals': 'Totals',
    'DashboardWebStringId.AccordionTab.Layout': 'Layout',
    'DashboardWebStringId.AccordionTab.PivotInitialState': 'Initial State',
    'DashboardWebStringId.AccordionTab.ImageOptions': 'Options',
    'DashboardWebStringId.AccordionTab.Labels': 'Labels',
    'DashboardWebStringId.AccordionTab.MapLegend': 'Color Legend',
    'DashboardWebStringId.AccordionTab.WeightedLegend': 'Weighted Legend',
    'DashboardWebStringId.AccordionTab.Common': 'Common',
    'DashboardWebStringId.AccordionTab.ChartLegend': 'Legend',
    'DashboardWebStringId.Grid.AutoFitToContentsColumnWidthMode': 'AutoFit To Content',
    'DashboardWebStringId.Grid.AutoFitToGridColumnWidthMode': 'AutoFitToGrid',
    'DashboardWebStringId.Grid.ManualGridColumnWidthMode': 'Manual',
    'DashboardStringId.DateTimeIntervalYear': 'Year',
    'DashboardStringId.DateTimeIntervalQuarter': 'Quarter',
    'DashboardStringId.DateTimeIntervalMonth': 'Month',
    'DashboardStringId.DateTimeIntervalWeek': 'Week',
    'DashboardStringId.DateTimeIntervalDay': 'Day',
    'DashboardStringId.DateTimeIntervalHour': 'Hour',
    'DashboardStringId.DateTimeIntervalMinute': 'Minute',
    'DashboardStringId.DateTimeIntervalSecond': 'Second',
    'DashboardStringId.DateTimeGroupIntervalYear': 'Year',
    'DashboardStringId.DateTimeGroupIntervalQuarter': 'Quarter',
    'DashboardStringId.DateTimeGroupIntervalMonth': 'Month',
    'DashboardStringId.DateTimeGroupIntervalDay': 'Day',
    'DashboardStringId.DateTimeGroupIntervalHour': 'Hour',
    'DashboardStringId.DateTimeGroupIntervalMinute': 'Minute',
    'DashboardStringId.DateTimeGroupIntervalSecond': 'Second',
    'DashboardStringId.DateTimeGroupIntervalDayOfYear': 'Day Of Year',
    'DashboardStringId.DateTimeGroupIntervalDayOfWeek': 'Day Of Week',
    'DashboardStringId.DateTimeGroupIntervalWeekOfYear': 'Week Of Year',
    'DashboardStringId.DateTimeGroupIntervalWeekOfMonth': 'Week Of Month',
    'DashboardStringId.DateTimeGroupIntervalMonthYear': 'Month-Year',
    'DashboardStringId.DateTimeGroupIntervalQuarterYear': 'Quarter-Year',
    'DashboardStringId.DateTimeGroupIntervalWeekYear': 'Week-Year',
    'DashboardStringId.DateTimeGroupIntervalDayMonthYear': 'Day-Month-Year',
    'DashboardStringId.DateTimeGroupIntervalDateHour': 'Date-Hour',
    'DashboardStringId.DateTimeGroupIntervalDateHourMinute': 'Date-Hour-Minute',
    'DashboardStringId.DateTimeGroupIntervalDateHourMinuteSecond': 'Date-Hour-Minute-Second',
    'DashboardStringId.DateTimeGroupIntervalExactDate': 'Exact Date',
    'DashboardStringId.GroupIntervalNone': 'No Grouping',
    'DashboardWebStringId.Treemap.StripedLayoutAlgorithm': 'Striped',
    'DashboardWebStringId.Treemap.SquarifiedLayoutAlgorithm': 'Squarified',
    'DashboardWebStringId.Treemap.SliceAndDiceLayoutAlgorithm': 'Slice and Dice',
    'DashboardWebStringId.Treemap.TileLabelsArgumentAndValue': 'Argument and Value',
    'DashboardWebStringId.Treemap.TileLabelsNone': 'None',
    'DashboardWebStringId.Treemap.TileLabelsValue': 'Value',
    'DashboardWebStringId.Treemap.TileLabelsArgument': 'Argument',
    'DashboardWebStringId.Treemap.TileTooltipsArgumentAndValue': 'Argument and Value',
    'DashboardWebStringId.Treemap.TileTooltipsArgument': 'Argument',
    'DashboardWebStringId.Treemap.TileTooltipsValue': 'Value',
    'DashboardWebStringId.Treemap.TileTooltipsNone': 'None',
    'DashboardWebStringId.Treemap.TopLeftToBottomRightLayoutDirection': 'Top Left To Bottom Right',
    'DashboardWebStringId.Treemap.TopRightToBottomLeftLayoutDirection': 'Top Right To Bottom Left',
    'DashboardWebStringId.Treemap.BottomLeftToTopRightLayoutDirection': 'Bottom Left To Top Right',
    'DashboardWebStringId.Treemap.BottomRightToTopLeftLayoutDirection': 'Bottom Right To Top Left',
    'DashboardWebStringId.Treemap.LayoutDirection': 'Layout Direction',
    'DashboardWebStringId.Treemap.GroupLabelsNone': 'None',
    'DashboardWebStringId.Treemap.GroupLabelsArgument': 'Argument',
    'DashboardWebStringId.Treemap.GroupLabelsValue': 'Value',
    'DashboardWebStringId.Treemap.GroupLabelsArgumentAndValue': 'Argument and Value',
    'DashboardWebStringId.Treemap.GroupTooltipsNone': 'None',
    'DashboardWebStringId.Treemap.GroupTooltipsArgument': 'Argument',
    'DashboardWebStringId.Treemap.GroupTooltipsValue': 'Value',
    'DashboardWebStringId.Treemap.GroupTooltipsArgumentAndValue': 'Argument and Value',
    'DashboardStringId.RankOrderAscending': 'Ascending',
    'DashboardStringId.RankOrderDescending': 'Descending',
    'DashboardStringId.DateTimeFormatYearFormatDefaultCaption': 'Default',
    'DashboardStringId.DateTimeFormatYearFormatFullCaption': 'Full',
    'DashboardStringId.DateTimeFormatYearFormatAbbreviatedCaption': 'Abbreviated',
    'DashboardStringId.DateTimeFormatQuarterFormatDefaultCaption': 'Default',
    'DashboardStringId.DateTimeFormatQuarterFormatNumericCaption': 'Numeric',
    'DashboardStringId.DateTimeFormatQuarterFormatFullCaption': 'Full',
    'DashboardStringId.DateTimeFormatMonthFormatDefaultCaption': 'Default',
    'DashboardStringId.DateTimeFormatMonthFormatFullCaption': 'Full',
    'DashboardStringId.DateTimeFormatMonthFormatAbbreviatedCaption': 'Abbreviated',
    'DashboardStringId.DateTimeFormatMonthFormatNumericCaption': 'Numeric',
    'DashboardStringId.DateTimeFormatDayOfWeekFormatDefaultCaption': 'Default',
    'DashboardStringId.DateTimeFormatDayOfWeekFormatFullCaption': 'Full',
    'DashboardStringId.DateTimeFormatDayOfWeekFormatAbbreviatedCaption': 'Abbreviated',
    'DashboardStringId.DateTimeFormatDayOfWeekFormatNumericCaption': 'Numeric',
    'DashboardStringId.DateTimeFormatDateFormatDefaultCaption': 'Default',
    'DashboardStringId.DateTimeFormatDateFormatLongCaption': 'Long',
    'DashboardStringId.DateTimeFormatDateFormatShortCaption': 'Short',
    'DashboardStringId.DateTimeFormatDateTimeFormatDefaultCaption': 'Default',
    'DashboardStringId.DateTimeFormatDateTimeFormatLongCaption': 'Long',
    'DashboardStringId.DateTimeFormatDateTimeFormatShortCaption': 'Short',
    'DashboardStringId.DateTimeFormatDateTimeFormatTimeOnlyCaption': 'TimeOnly',
    'DashboardStringId.ExactDateFormatYear': 'Year',
    'DashboardStringId.ExactDateFormatQuarter': 'Quarter',
    'DashboardStringId.ExactDateFormatMonth': 'Month',
    'DashboardStringId.ExactDateFormatDay': 'Day',
    'DashboardStringId.ExactDateFormatHour': 'Hour',
    'DashboardStringId.ExactDateFormatMinute': 'Minute',
    'DashboardStringId.ExactDateFormatSecond': 'Second',
    'DashboardWebStringId.FormatType': 'Format Type',
    'DashboardWebStringId.TopN.Top': 'Top',
    'DashboardWebStringId.TopN.Bottom': 'Bottom',
    'DashboardWebStringId.DimensionSortBy': 'Sort by',
    'DashboardStringId.NumericFormatUnitAutoCaption': 'Auto',
    'DashboardStringId.NumericFormatFormatTypeAutoCaption': 'Auto',
    'DashboardStringId.NumericFormatFormatTypeGeneralCaption': 'General',
    'DashboardStringId.NumericFormatFormatTypeNumberCaption': 'Number',
    'DashboardStringId.NumericFormatFormatTypeCurrencyCaption': 'Currency',
    'DashboardStringId.NumericFormatFormatTypeScientificCaption': 'Scientific',
    'DashboardStringId.NumericFormatFormatTypePercentCaption': 'Percent',
    'DashboardStringId.NumericFormatUnitOnesCaption': 'Ones',
    'DashboardStringId.NumericFormatUnitThousandsCaption': 'Thousands',
    'DashboardStringId.NumericFormatUnitMillionsCaption': 'Millions',
    'DashboardStringId.NumericFormatUnitBillionsCaption': 'Billions',
    'DashboardStringId.DeltaValueTypeActualValueCaption': 'Actual Value',
    'DashboardStringId.DeltaValueTypeAbsoluteVariationCaption': 'Absolute Variation',
    'DashboardStringId.DeltaValueTypePercentVariationCaption': 'Percent Variation',
    'DashboardStringId.DeltaValueTypePercentOfTargetCaption': 'Percent Of Target',
    'DashboardStringId.DeltaValueTypeTargetValueCaption': 'Target Value',
    'DashboardStringId.GaugeScaleLabelFormatCaption': 'Scale Label',
    'DashboardStringId.DeltaIndicationModeGreaterIsGoodCaption': 'Greater Is Good',
    'DashboardStringId.DeltaIndicationModeLessIsGoodCaption': 'Less Is Good',
    'DashboardStringId.DeltaIndicationModeWarningIfGreaterCaption': 'Warning If Greater',
    'DashboardStringId.DeltaIndicationModeWarningIfLessCaption': 'Warning If Less',
    'DashboardStringId.DeltaIndicationModeNoIndicationCaption': 'No Indication',
    'DashboardStringId.DeltaThresholdTypeAbsolute': 'Absolute',
    'DashboardStringId.DeltaThresholdTypePercent': 'Percent',
    'DashboardStringId.CommandDimensionGroupChildValues': 'Group Tiles',
    'DashboardStringId.SeriesTypeBar': 'Bar',
    'DashboardStringId.SeriesTypeStackedBar': 'Stacked Bar',
    'DashboardStringId.SeriesTypeFullStackedBar': 'Full Stacked Bar',
    'DashboardStringId.SeriesTypePoint': 'Point',
    'DashboardStringId.SeriesTypeLine': 'Line',
    'DashboardStringId.SeriesTypeStackedLine': 'Stacked Line',
    'DashboardStringId.SeriesTypeFullStackedLine': 'Full Stacked Line',
    'DashboardStringId.SeriesTypeStepLine': 'Step Line',
    'DashboardStringId.SeriesTypeSpline': 'Spline',
    'DashboardStringId.SeriesTypeArea': 'Area',
    'DashboardStringId.SeriesTypeStackedArea': 'Stacked Area',
    'DashboardStringId.SeriesTypeFullStackedArea': 'Full Stacked Area',
    'DashboardStringId.SeriesTypeStepArea': 'Step Area',
    'DashboardStringId.SeriesTypeSplineArea': 'Spline Area',
    'DashboardStringId.SeriesTypeStackedSplineArea': 'Stacked Spline Area',
    'DashboardStringId.SeriesTypeFullStackedSplineArea': 'Full Stacked Spline Area',
    'DashboardWebStringId.Chart.ShowLegend': 'Show Legend',
    'DashboardStringId.DefaultNameBoundImageItem': 'Bound Image',
    'DashboardStringId.DefaultNameImageItem': 'Image',
    'DashboardStringId.DefaultNameTextBoxItem': 'Text Box',
    'DashboardStringId.DefaultNameCardItem': 'Cards',
    'DashboardStringId.DefaultNameChartItem': 'Chart',
    'DashboardStringId.DefaultNameComboBoxItem': 'Combo Box',
    'DashboardStringId.DefaultNameListBoxItem': 'List Box',
    'DashboardStringId.DefaultNameTreeViewItem': 'Tree View',
    'DashboardStringId.DefaultNameGaugeItem': 'Gauges',
    'DashboardStringId.DefaultNameGridItem': 'Grid',
    'DashboardStringId.GridHyperlinkUriPatternErrorMessage': 'Invalid placeholder',
    'DashboardWebStringId.DefaultNameBubbleMapItem': 'Bubble Map',
    'DashboardWebStringId.DefaultNameChoroplethMapItem': 'Choropleth Map',
    'DashboardWebStringId.DefaultNameGeoPointMapItem': 'Geo Point Map',
    'DashboardWebStringId.DefaultNamePieMapItem': 'Pie Map',
    'DashboardStringId.DefaultNamePieItem': 'Pies',
    'DashboardStringId.DefaultNamePivotItem': 'Pivot',
    'DashboardStringId.DefaultNameRangeFilterItem': 'Range Filter',
    'DashboardStringId.DefaultNameScatterChartItem': 'Scatter Chart',
    'DashboardStringId.DefaultNameTreemapItem': 'Treemap',
    'DashboardStringId.DescriptionMaps': 'Maps',
    'DashboardStringId.EntireRangePeriodCaption': 'Entire Range',
    'DashboardStringId.CalculationTypePercentOfTotal': 'Percent Of Total',
    'DashboardStringId.CalculationTypeDifference': 'Difference',
    'DashboardStringId.CalculationTypePercentageDifference': 'Percentage Difference',
    'DashboardStringId.CalculationTypeRunningTotal': 'Running Total',
    'DashboardStringId.CalculationTypeMovingCalculation': 'Moving Calculation',
    'DashboardStringId.CalculationTypeRank': 'Rank',
    'DashboardStringId.ColorPaletteIndex': 'Palette Index',
    'DashboardStringId.DifferenceCalculationTargetPrevious': 'Previous',
    'DashboardStringId.DifferenceCalculationTargetNext': 'Next',
    'DashboardStringId.DifferenceCalculationTargetFirst': 'First',
    'DashboardStringId.DifferenceCalculationTargetLast': 'Last',
    'DashboardStringId.SummaryTypeCount': 'Count',
    'DashboardStringId.SummaryTypeSum': 'Sum',
    'DashboardStringId.SummaryTypeMin': 'Min',
    'DashboardStringId.SummaryTypeMax': 'Max',
    'DashboardStringId.SummaryTypeAverage': 'Average',
    'DashboardStringId.SummaryTypeStdDev': 'StdDev',
    'DashboardStringId.SummaryTypeStdDevp': 'StdDevp',
    'DashboardStringId.SummaryTypeVar': 'Var',
    'DashboardStringId.SummaryTypeVarp': 'Varp',
    'DashboardStringId.SummaryTypeCountDistinct': 'Count Distinct',
    'DashboardStringId.SummaryTypeMedian': 'Median',
    'DashboardStringId.SummaryTypeMode': 'Mode',
    'DashboardStringId.RankTypeUnique': 'Unique',
    'DashboardStringId.RankTypeCompetition': 'Competition',
    'DashboardStringId.RankTypeDense': 'Dense',
    'DashboardStringId.RankTypeModified': 'Modified',
    'DashboardStringId.RankTypePercentile': 'Percentile',
    'DashboardStringId.CardCalculationAlongCards': 'Cards',
    'DashboardStringId.CardCalculationAlongSparklineArgument': 'Sparkline Argument',
    'DashboardStringId.CardCalculationAlongCardsAndSparklineArgument': 'Cards And Sparkline Argument',
    'DashboardStringId.CardCalculationAlongSparklineArgumentAndCards': 'Sparkline Argument And Cards',
    'DashboardStringId.ChartCalculationAlongArguments': 'Arguments',
    'DashboardStringId.ChartCalculationAlongSeries': 'Series',
    'DashboardStringId.ChartCalculationAlongArgumentsAndSeries': 'Arguments And Series',
    'DashboardStringId.ChartCalculationAlongSeriesAndArguments': 'Series And Arguments',
    'DashboardStringId.GridCalculationAlongRows': 'Rows',
    'DashboardStringId.GridCalculationAlongSparklineArgument': 'Sparkline Argument',
    'DashboardStringId.GridCalculationAlongRowsAndSparklineArgument': 'Rows And Sparkline Argument',
    'DashboardStringId.GridCalculationAlongSparklineArgumentAndRows': 'Sparkline Argument And Rows',
    'DashboardStringId.PivotCalculationAlongColumns': 'Columns',
    'DashboardStringId.PivotCalculationAlongRows': 'Rows',
    'DashboardStringId.PivotCalculationAlongColumnsAndRows': 'Columns And Rows',
    'DashboardStringId.PivotCalculationAlongRowsAndColumns': 'Rows And Columns',
    'DashboardStringId.PivotCalculationAlongGroupsInColumns': 'Groups In Columns',
    'DashboardStringId.PivotCalculationAlongGroupsInRows': 'Groups In Rows',
    'DashboardStringId.PivotCalculationAlongGroupsInColumnsAndRows': 'Groups In Columns And Rows',
    'DashboardStringId.PivotCalculationAlongGroupsInRowsAndColumns': 'Groups In Rows And Columns',
    'DashboardStringId.PieCalculationAlongArguments': 'Arguments',
    'DashboardStringId.PieCalculationAlongSeries': 'Series',
    'DashboardStringId.PieCalculationAlongArgumentsAndSeries': 'Arguments / Series',
    'DashboardStringId.PieCalculationAlongSeriesAndArguments': 'Series / Arguments',
    'DashboardStringId.RangeFilterCalculationAlongArgumentAndSeries': 'Argument / Series',
    'DashboardStringId.RangeFilterCalculationAlongSeriesAndArgument': 'Series / Argument',
    'DashboardStringId.GaugeCalculationAlongGauges': 'Gauges',
    'DashboardStringId.ScatterCalculationAlongArguments': 'Arguments',
    'DashboardStringId.CalculationAlongSpecificDimensions': 'Specific...',
    'DashboardStringId.CalculatedFieldTypeString': 'String',
    'DashboardStringId.CalculatedFieldTypeLong': 'Integer',
    'DashboardStringId.CalculatedFieldTypeDecimal': 'Decimal',
    'DashboardStringId.CalculatedFieldTypeBoolean': 'Boolean',
    'DashboardStringId.CalculatedFieldTypeDateTime': 'DateTime',
    'DashboardStringId.CalculatedFieldTypeDouble': 'Double',
    'DashboardStringId.CalculatedFieldTypeAuto': 'Auto',
    'DashboardStringId.CalculatedFieldTypeObject': 'Object',
    'DashboardStringId.DefaultEFDataSourceName': 'EF Data Source',
    'DashboardStringId.DefaultExcelDataSourceName': 'Excel Data Source',
    'DashboardStringId.DefaultFileExtractDataSourceName': 'Extract Data Source',
    'DashboardStringId.DefaultFederationDataSourceName': 'Federated Data Source',
    'DashboardStringId.DefaultObjectDataSourceName': 'Object Data Source',
    'DashboardStringId.DefaultOlapDataSourceName': 'OLAP Data Source',
    'DashboardStringId.DefaultSqlDataSourceName': 'SQL Data Source',
    'DashboardStringId.DefaultJsonDataSourceName': 'JSON Data Source',
    'DashboardStringId.DefaultMongoDBDataSourceName': 'MongoDB Data Source',
    'DashboardStringId.DefaultXpoDataSourceName': 'XPO Data Source',
    'DashboardStringId.OlapRaggedHierarchyNoneItemCaption': '(None)',
    'DashboardStringId.FormatRuleApplyToColumn': 'Apply to column',
    'DashboardStringId.SeriesTypeSideBySideRangeBar': 'Side By Side Range Bar',
    'DashboardStringId.SeriesTypeRangeArea': 'Range Area',
    'DashboardStringId.SeriesTypeHighLowClose': 'High Low Close',
    'DashboardStringId.SeriesTypeCandleStick': 'Candle Stick',
    'DashboardStringId.SeriesTypeStock': 'Type Stock',
    'DashboardWebStringId.StyleSettings': 'Style',
    'DashboardStringId.ParametersFormCalendarTodayButton': 'Today',
    'DashboardStringId.TreemapLabelArgumentTemplate': '{0} - {1}',
    'DashboardStringId.TreemapLabelValueTemplate': '{0}: {1}',
    'DashboardStringId.SearchNullValuePrompt': 'Enter text to search...',
    'DashboardStringId.CustomItems': 'Custom Items',
    'DashboardStringId.FormatRuleAllowNegativeAxis': 'Allow negative axis',
    'DashboardStringId.FormatRuleDrawAxis': 'Draw axis',
    'DashboardStringId.FormatRuleShowBarOnly': 'Show bar only',
    'DashboardStringId.CommandFormatRuleBottomN': 'Bottom N',
    'DashboardStringId.FormatConditionAppearanceNone': 'None',
    'DashboardStringId.FormatConditionAppearanceCustom': 'Custom',
    'DashboardStringId.FormatConditionAppearancePaleRed': 'Pale Red',
    'DashboardStringId.FormatConditionAppearancePaleYellow': 'Pale Yellow',
    'DashboardStringId.FormatConditionAppearancePaleGreen': 'Pale Green',
    'DashboardStringId.FormatConditionAppearancePaleBlue': 'Pale Blue',
    'DashboardStringId.FormatConditionAppearancePalePurple': 'Pale Purple',
    'DashboardStringId.FormatConditionAppearancePaleCyan': 'Pale Cyan',
    'DashboardStringId.FormatConditionAppearancePaleOrange': 'Pale Orange',
    'DashboardStringId.FormatConditionAppearancePaleGray': 'Pale Gray',
    'DashboardStringId.FormatConditionAppearanceRed': 'Red',
    'DashboardStringId.FormatConditionAppearanceYellow': 'Yellow',
    'DashboardStringId.FormatConditionAppearanceGreen': 'Green',
    'DashboardStringId.FormatConditionAppearanceBlue': 'Blue',
    'DashboardStringId.FormatConditionAppearancePurple': 'Purple',
    'DashboardStringId.FormatConditionAppearanceCyan': 'Cyan',
    'DashboardStringId.FormatConditionAppearanceOrange': 'Orange',
    'DashboardStringId.FormatConditionAppearanceGray': 'Gray',
    'DashboardStringId.FormatConditionAppearanceGradientRed': 'Gradient Red',
    'DashboardStringId.FormatConditionAppearanceGradientYellow': 'Gradient Yellow',
    'DashboardStringId.FormatConditionAppearanceGradientGreen': 'Gradient Green',
    'DashboardStringId.FormatConditionAppearanceGradientBlue': 'Gradient Blue',
    'DashboardStringId.FormatConditionAppearanceGradientPurple': 'Gradient Purple',
    'DashboardStringId.FormatConditionAppearanceGradientCyan': 'Gradient Cyan',
    'DashboardStringId.FormatConditionAppearanceGradientOrange': 'Gradient Orange',
    'DashboardStringId.FormatConditionAppearanceGradientTransparent': 'Gradient Transparent',
    'DashboardStringId.FormatConditionAppearanceFontBold': 'Bold',
    'DashboardStringId.FormatConditionAppearanceFontItalic': 'Italic',
    'DashboardStringId.FormatConditionAppearanceFontUnderline': 'Underline',
    'DashboardStringId.FormatConditionAppearanceFontGrayed': 'Grayed',
    'DashboardStringId.FormatConditionAppearanceFontRed': 'Red',
    'DashboardStringId.FormatConditionAppearanceFontYellow': 'Yellow',
    'DashboardStringId.FormatConditionAppearanceFontGreen': 'Green',
    'DashboardStringId.FormatConditionAppearanceFontBlue': 'Blue',
    'DashboardStringId.IntersectionLevelModeFirst': 'First level',
    'DashboardStringId.IntersectionLevelModeLast': 'Last level',
    'DashboardStringId.IntersectionLevelModeAuto': '(Auto)',
    'DashboardStringId.IntersectionLevelModeAll': 'All levels',
    'DashboardStringId.IntersectionLevelModeSpecific': 'Specific level',
    'DashboardStringId.FormatRuleApplyToRow': 'Apply to row',
    'DashboardStringId.FormatRuleApplyTo': 'Apply to',
    'DashboardStringId.FormatRuleApplyToChartElement': 'Apply to chart elements',
    'DashboardStringId.ChartElementPointCaption': 'Point',
    'DashboardStringId.ChartElementLineOrAreaCaption': 'Line/Area',
    'DashboardWebStringId.FilterItem.AllowEmptyFilter': 'Allow Empty Filter',
    'DashboardStringId.FormatConditionAppearanceFontStrikeout': 'Strikeout',
    'DashboardStringId.FormatConditionAutomaticValueType': 'Automatic',
    'DashboardStringId.FormatConditionNumberValueType': 'Number',
    'DashboardStringId.FormatConditionPercentValueType': 'Percent',
    'DashboardStringId.CommandFormatRuleRangeIcons': 'Range Icons',
    'DashboardStringId.CommandFormatRuleGreaterThan': 'Greater',
    'DashboardStringId.CommandFormatRuleGreaterThanOrEqualTo': 'Greater Or Equal',
    'DashboardStringId.CommandFormatRuleLessThan': 'Less',
    'DashboardStringId.CommandFormatRuleLessThanOrEqualTo': 'Less Or Equal',
    'DashboardStringId.CommandFormatRuleEqualTo': 'Equal',
    'DashboardStringId.CommandFormatRuleNotEqualTo': 'Not Equal',
    'DashboardStringId.CommandFormatRuleBetween': 'Between',
    'DashboardStringId.CommandFormatRuleNotBetween': 'Not Between',
    'DashboardStringId.CommandFormatRuleBetweenOrEqual': 'Between Or Equal',
    'DashboardStringId.CommandFormatRuleNotBetweenOrEqual': 'Not Between Or Equal',
    'DashboardStringId.CommandFormatRuleContains': 'Contains Text',
    'DashboardStringId.ColorAutoAssigned': 'Auto',
    'DashboardStringId.CalculationTypeNone': 'None',
    'DashboardStringId.UndoText': 'Undo',
    'DashboardStringId.RedoText': 'Redo',
    'DashboardStringId.DashboardCurrencyUseCurrentCurrency': 'Use client system settings',
    'DashboardStringId.CommandDimensionSortModeDisplayText': '(Display Text)',
    'DashboardStringId.CommandDimensionSortModeValue': '(Value)',
    'DashboardStringId.CommandDimensionSortModeID': '(ID)',
    'DashboardStringId.CommandDimensionSortModeKey': '(Key)',
    'DashboardWebStringId.Chart.AddPane': 'Add Pane',
    'DashboardStringId.MapShapeNoneAttribute': 'NONE',
    'DashboardStringId.DefaultPeriodName': 'Period',
    'DashboardStringId.CardLayoutTemplateStretchedCaption': 'Stretched',
    'DashboardStringId.CardLayoutTemplateCenteredCaption': 'Centered',
    'DashboardStringId.CardLayoutTemplateCompactCaption': 'Compact',
    'DashboardStringId.CardLayoutTemplateLightweightCaption': 'Lightweight',
    'DashboardStringId.CardLayoutTemplateCustomCaption': 'Custom',
    'DashboardStringId.CardLayoutTemplateNoneCaption': 'None',
    'DashboardStringId.CardRowDataElementTypeTitleCaption': 'Title',
    'DashboardStringId.CardRowDataElementTypeSubtitleCaption': 'Subtitle',
    'DashboardStringId.CardRowDataElementTypeActualValueCaption': 'Actual Value',
    'DashboardStringId.CardRowDataElementTypeTargetValueCaption': 'Target Value',
    'DashboardStringId.CardRowDataElementTypeAbsoluteVariationCaption': 'Absolute Variation',
    'DashboardStringId.CardRowDataElementTypePercentVariationCaption': 'Percent Variation',
    'DashboardStringId.CardRowDataElementTypePercentOfTargetCaption': 'Percent of Target',
    'DashboardStringId.CardRowDataElementTypeDimensionValueCaption': 'Dimension',
    'DashboardStringId.CardRowDataElementTypeCardNameCaption': 'Card Name',
    'DashboardStringId.CardRowDataElementTypeDeltaIndicatorCaption': 'Delta Indicator',
    'DashboardStringId.CardRowDataElementTypeSparklineCaption': 'Sparkline',
    'DashboardStringId.FormatRuleApplyToAllElements': 'All elements',
    'DashboardWebStringId.FormatRuleApplyToDimensionElement': 'Dimension',
    'DashboardWebStringId.FormatRuleApplyToTextElement': 'Text',
    'DashboardStringId.FormatRuleDisplayInLegend': 'Display in legend',
    'DashboardStringId.FormatRuleDisplayName': 'Caption',
    'DashboardStringId.LegendItemFormatString': '{0} - {1}',
    'DashboardWebStringId.Pie.LabelPosition': 'Label Position',
    'DashboardWebStringId.Pie.LabelPositionOutside': 'Outside',
    'DashboardWebStringId.Pie.LabelPositionInside': 'Inside',
    'DashboardStringId.IncludeExportDashboardState': 'Include',
    'DashboardStringId.PointLabelContentTypeArgument': 'Argument',
    'DashboardStringId.PointLabelContentTypeSeriesName': 'Series Name',
    'DashboardStringId.PointLabelContentTypeValue': 'Value',
    'DashboardStringId.PointLabelContentTypePercent': 'Percent',
    'DashboardWebStringId.DashboardMenu': 'Dashboard Menu',
    'DashboardWebStringId.DashboardMenuNew': 'New...',
    'DashboardWebStringId.DashboardMenuOpen': 'Open...',
    'DashboardWebStringId.DashboardMenuSave': 'Save',
    'DashboardWebStringId.DashboardMenuCurrency': 'Currency',
    'DashboardWebStringId.DashboardMenuDataSources': 'Data Sources',
    'DashboardWebStringId.DashboardMenuColorScheme': 'Color Scheme',
    'DashboardWebStringId.DashboardMenuNewDashboardCreate': 'Create',
    'DashboardWebStringId.DashboardMenuNewDashboardNamePlaceholder': 'Enter a dashboard name',
    'DashboardWebStringId.Binding.DefaultEmptyPlaceholder': 'Set {0}',
    'DashboardWebStringId.Binding.DefaultSelectedPlaceholder': 'Configure {0}',
    'DashboardWebStringId.Binding.DataSource': 'Data Source',
    'DashboardWebStringId.Binding.Argument': 'Argument ',
    'DashboardWebStringId.Binding.Attribute': 'Attribute ',
    'DashboardWebStringId.Binding.Sparkline': 'Sparkline ',
    'DashboardWebStringId.Binding.Columns': 'Columns ',
    'DashboardWebStringId.Binding.TooltipDimensions': 'Tooltip Dimensions',
    'DashboardWebStringId.Binding.TooltipMeasures': 'Tooltip Measures',
    'DashboardWebStringId.Binding.HiddenDimensions': 'Hidden Dimensions',
    'DashboardWebStringId.Binding.HiddenMeasures': 'Hidden Measures',
    'DashboardWebStringId.Binding.AddDimension': 'Add Dimension',
    'DashboardWebStringId.Binding.AddMeasure': 'Add Measure',
    'DashboardWebStringId.Binding.AddColumn': 'Add Column',
    'DashboardWebStringId.Binding.AddRow': 'Add Row',
    'DashboardWebStringId.Binding.AddValue': 'Add Value',
    'DashboardWebStringId.Binding.AddSeries': 'Add Series',
    'DashboardWebStringId.Binding.SetLatitude': 'Set Latitude',
    'DashboardWebStringId.Binding.SetLongitude': 'Set Longitude',
    'DashboardWebStringId.Binding.SetArgument': 'Set Argument',
    'DashboardWebStringId.Binding.AddArgument': 'Add Argument',
    'DashboardWebStringId.Binding.SetWeight': 'Set Weight',
    'DashboardWebStringId.Binding.SetColor': 'Set Color',
    'DashboardWebStringId.Binding.SetAttribute': 'Set Attribute',
    'DashboardWebStringId.Binding.SetValue': 'Set Value',
    'DashboardWebStringId.Binding.XAxis': 'X - axis ',
    'DashboardWebStringId.Binding.YAxis': 'Y - axis ',
    'DashboardWebStringId.Binding.ConfigureDimension': 'Configure Dimension',
    'DashboardWebStringId.Binding.ConfigureMeasure': 'Configure Measure',
    'DashboardWebStringId.Binding.ConfigureSeries': 'Configure Series',
    'DashboardWebStringId.Binding.ConfigureArgument': 'Configure Argument',
    'DashboardWebStringId.Binding.ConfigureValue': 'Configure Value',
    'DashboardWebStringId.Binding.ConfigureAttribute': 'Configure Attribute',
    'DashboardWebStringId.Binding.ConfigureLatitude': 'Configure Latitude',
    'DashboardWebStringId.Binding.ConfigureLongitude': 'Configure Longitude',
    'DashboardWebStringId.Binding.ConfigureColumn': 'Configure Column',
    'DashboardWebStringId.Binding.ConfigureRow': 'Configure Row',
    'DashboardWebStringId.Binding.ConfigureWeight': 'Configure Weight',
    'DashboardWebStringId.Binding.ConfigureColor': 'Configure Color',
    'DashboardWebStringId.Binding.ShowAll': 'Show All',
    'DashboardWebStringId.ColorScheme.Color': 'Color',
    'DashboardWebStringId.ColorScheme.Colors': 'Colors',
    'DashboardWebStringId.Parameters.ParameterList': 'Parameter List',
    'DashboardWebStringId.Parameters.AddParameter': 'Add New Parameter',
    'DashboardWebStringId.Parameters.AddParameter.Tooltip': 'Add new parameter to the dashboard',
    'DashboardWebStringId.Parameters.NoSelectedParameter': 'No parameter is selected.',
    'DashboardWebStringId.Parameters.Reorder': 'Reorder',
    'DashboardWebStringId.Parameters.Done': 'Done',
    'DashboardWebStringId.Currency.Currency': 'Currency',
    'DashboardWebStringId.Currency.CurrencyCulture': 'Culture',
    'DashboardWebStringId.Currency.DefaultCurrencyWarning': 'The currency culture format is based on environmental settings. Specify the culture explicitly to ignore these settings.',
    'DashboardWebStringId.Preview': 'Preview',
    'DashboardWebStringId.Add': 'Add',
    'DashboardWebStringId.DataSources.UsedDataSources': 'Used Data Sources',
    'DashboardWebStringId.DataSources.DataItems': 'Data Items',
    'DashboardWebStringId.DataSources.AddQuery': 'Add Query',
    'DashboardWebStringId.DataSources.ManageQueries': 'Manage Queries',
    'DashboardWebStringId.DataSources.AddCalculatedField': 'Add Calculated Field',
    'DashboardWebStringId.DataSources.AddDataSource': 'Add Data Source',
    'DashboardWebStringId.DataSources.RenameDataSource': 'Rename Data Source',
    'DashboardWebStringId.DataSources.NoUsedDataSources': 'There are no used data sources in this dashboard.',
    'DashboardWebStringId.DataSources.NoAvailableDataSources': 'There are no available datasources.',
    'DashboardWebStringId.DataSources.ChooseDataSource': 'Choose the data source for the dashboard',
    'DashboardWebStringId.DataSources.CreateDataSource': 'Create',
    'DashboardWebStringId.DataSources.CreateNewDataSource': 'Create data source...',
    'DashboardWebStringId.DataSources.CreateDataSource.Tooltip': 'Create a new data source...',
    'DashboardWebStringId.DataSources.EditCalculatedField': 'Edit Calculated Field',
    'DashboardWebStringId.DataSources.CalculatedField.Name': 'Name ',
    'DashboardWebStringId.DataSources.CalculatedField.FieldType': 'Field Type',
    'DashboardWebStringId.DataSources.CalculatedField.UniqueNameError': 'Calculated field should have a unique name.',
    'DashboardWebStringId.DataSources.CalculatedField.Corrupted': 'The calculated field contains errors',
    'DashboardWebStringId.DataSources.Edit': 'Edit',
    'DashboardWebStringId.DataSources.Rename': 'Rename',
    'DashboardWebStringId.DataSources.NewDataSourceName': 'New Data Source Name:',
    'DashboardWebStringId.DataSources.DataSourceNameExistsMessage': 'Duplicate Name: Please enter a unique data source name to proceed.',
    'DashboardStringId.MessageCollectionTypesNotSupported': 'Dashboard does not support collection types.',
    'DashboardWebStringId.New.DashboardName': 'Dashboard Name:',
    'DashboardWebStringId.Bindings': 'Binding',
    'DashboardWebStringId.Interactivity': 'Interactivity',
    'DashboardWebStringId.Filters': 'Filters',
    'DashboardWebStringId.Options': 'Options',
    'DashboardWebStringId.Remove': 'Remove',
    'DashboardWebStringId.MasterFilterMode': 'Master Filter Mode',
    'DashboardWebStringId.Title.Text': 'Text',
    'DashboardWebStringId.Title.Visible': 'Visible',
    'DashboardWebStringId.Title.IncludeMasterFilter': 'Include Master Filter',
    'DashboardWebStringId.Title.Aligment': 'Alignment',
    'DashboardWebStringId.Title.Image': 'Image',
    'DashboardWebStringId.DataSources.Cancel': 'Cancel',
    'DashboardWebStringId.DataSources.IncludeToTheDashboard': 'Add',
    'DashboardWebStringId.DataSources.AvailableDataSourcesError': 'An error occurred during an attempt to load the list of available data sources',
    'DashboardWebStringId.DataSources.DashboardDataSourceWizard': 'Dashboard Data Source Wizard',
    'DashboardWebStringId.Viewer': 'Viewer ',
    'DashboardWebStringId.Loading': 'LOADING...',
    'DashboardWebStringId.ClickHereInvitation': 'Click here',
    'DashboardWebStringId.BindDataInvitation': 'to bind this item to data',
    'DashboardWebStringId.AddItemInvitation': 'Drag an item from the Toolbox or click a button below to create a new item.',
    'DashboardWebStringId.Parameters.Values': 'Values ',
    'DashboardWebStringId.Binding.Cards': 'Cards ',
    'DashboardWebStringId.Binding.AddCard': 'Add Card',
    'DashboardWebStringId.Binding.ConfigureCard': 'Configure Card',
    'DashboardWebStringId.Binding.AddValues': 'Add Values',
    'DashboardWebStringId.Binding.ConfigureValues': 'Configure Values',
    'DashboardWebStringId.Binding.Maps': 'Maps ',
    'DashboardWebStringId.Binding.AddMap': 'Add Map',
    'DashboardWebStringId.Binding.ConfigureMap': 'Configure Map',
    'DashboardWebStringId.Binding.Gauges': 'Gauges ',
    'DashboardWebStringId.Binding.AddGauge': 'Add Gauge',
    'DashboardWebStringId.Binding.ConfigureGauge': 'Configure Gauge',
    'DashboardWebStringId.InteractivityOptions': 'Interactivity Options',
    'DashboardWebStringId.Binding.Values': 'Values ',
    'DashboardWebStringId.Binding.RemovePane': 'Remove Pane',
    'DashboardWebStringId.ClickToSetFilterCriteria': 'Click to set Filter Criteria',
    'DashboardWebStringId.DataSources.OK': 'OK',
    'DashboardWebStringId.DataSources.Save': 'Save',
    'DashboardWebStringId.Cards.ArrangementMode': 'Content Arrangement Mode',
    'DashboardWebStringId.Cards.ArrangementMode.Auto': 'Auto',
    'DashboardWebStringId.Cards.ArrangementMode.FixedRows': 'Fixed Rows',
    'DashboardWebStringId.Cards.ArrangementMode.FixedColumns': 'Fixed Columns',
    'DashboardWebStringId.Cards.LineCount': 'Line Count',
    'DashboardWebStringId.ConditionalFormatting': 'Conditional Formatting',
    'DashboardWebStringId.CollectionEditor.NoItems': 'Click + to add a new item.',
    'DashboardWebStringId.CollectionEditor.Totals.NoItems': 'Click + to add a new total.',
    'DashboardWebStringId.CollectionEditor.Parameters.NoItems': 'Click + to add a new parameter.',
    'DashboardWebStringId.PivotGrid.AutoExpandColumnGroups': 'Auto - Expand Column Groups',
    'DashboardWebStringId.PivotGrid.AutoExpandRowGroups': 'Auto - Expand Row Groups',
    'DashboardWebStringId.PivotGrid.ColumnGrandTotals': 'Column Grand Totals',
    'DashboardWebStringId.PivotGrid.RowGrandTotals': 'Row Grand Totals',
    'DashboardWebStringId.PivotGrid.ColumnTotals': 'Column Totals',
    'DashboardWebStringId.PivotGrid.RowTotals': 'Row Totals',
    'DashboardWebStringId.PivotGrid.ValuesPosition': 'Values Position',
    'DashboardWebStringId.PivotGrid.ValuesPositionColumns': 'Columns',
    'DashboardWebStringId.PivotGrid.ValuesPositionRows': 'Rows',
    'DashboardWebStringId.PivotGrid.RowTotalsPosition': 'Row Totals Position',
    'DashboardWebStringId.PivotGrid.RowTotalsPositionBottom': 'Bottom',
    'DashboardWebStringId.PivotGrid.RowTotalsPositionTop': 'Top',
    'DashboardWebStringId.PivotGrid.ColumnTotalsPosition': 'Column Totals Position',
    'DashboardWebStringId.PivotGrid.ColumnTotalsPositionFar': 'Far',
    'DashboardWebStringId.PivotGrid.ColumnTotalsPositionNear': 'Near',
    'DashboardWebStringId.PivotGrid.LayoutType': 'Layout',
    'DashboardWebStringId.PivotGrid.LayoutTypeCompact': 'Compact',
    'DashboardWebStringId.PivotGrid.LayoutTypeTabular': 'Tabular',
    'DashboardWebStringId.Options.Caption': 'Caption',
    'DashboardWebStringId.InteractivityOptions.IsMasterFilter': 'Master Filter',
    'DashboardWebStringId.InteractivityOptions.MasterFilterMode.None': 'None',
    'DashboardWebStringId.InteractivityOptions.MasterFilterMode.Single': 'Single',
    'DashboardWebStringId.InteractivityOptions.MasterFilterMode.Multiple': 'Multiple',
    'DashboardWebStringId.InteractivityOptions.TargetDimensions': 'Target Dimensions',
    'DashboardWebStringId.InteractivityOptions.TargetDimensions.Arguments': 'Arguments',
    'DashboardWebStringId.InteractivityOptions.TargetDimensions.Series': 'Series',
    'DashboardWebStringId.InteractivityOptions.TargetDimensions.Points': 'Points',
    'DashboardWebStringId.ColoringOptions': 'Coloring',
    'DashboardWebStringId.DataSources.Filter': 'Filter',
    'DashboardWebStringId.Chart.Rotated': 'Rotated',
    'DashboardWebStringId.Chart.Panes': 'Panes',
    'DashboardWebStringId.Chart.Legend': 'Legend',
    'DashboardWebStringId.Chart.AxisX': 'Axis X',
    'DashboardWebStringId.Chart.AxisY': 'Axis Y',
    'DashboardWebStringId.Chart.AxisYSecondary': 'Axis Y Secondary',
    'DashboardWebStringId.Chart.PointLabelOptions': 'Point Label Options',
    'DashboardWebStringId.Chart.AxisXMeasure': 'Axis X Measure',
    'DashboardWebStringId.Chart.AxisYMeasure': 'Axis Y Measure',
    'DashboardWebStringId.Chart.Weight': 'Weight',
    'DashboardWebStringId.Chart.Content': 'Content',
    'DashboardWebStringId.Chart.ArgumentAndWeight': 'Argument and Weight',
    'DashboardWebStringId.Chart.ArgumentAndValues': 'Argument and Values',
    'DashboardWebStringId.Chart.Reverse': 'Reverse',
    'DashboardWebStringId.Chart.Visible': 'Visible',
    'DashboardWebStringId.Chart.Title': 'Title',
    'DashboardWebStringId.Chart.TitleText': 'Title Text',
    'DashboardWebStringId.Chart.EnableZooming': 'Enable Zooming',
    'DashboardWebStringId.Chart.LimitVisiblePoints': 'Limit visible points',
    'DashboardWebStringId.Chart.VisiblePointsCount': 'Visible Points Count',
    'DashboardWebStringId.Chart.AlwaysShowZeroLevel': 'Always show zero level',
    'DashboardWebStringId.Chart.GridLines': 'Grid Lines',
    'DashboardWebStringId.Chart.LogarithmicScale': 'Logarithmic Scale',
    'DashboardWebStringId.Chart.LogarithmicScaleBase': 'Logarithmic Scale Base',
    'DashboardWebStringId.Chart.LogarithmicScaleBase2': '2',
    'DashboardWebStringId.Chart.LogarithmicScaleBase5': '5',
    'DashboardWebStringId.Chart.LogarithmicScaleBase10': '10',
    'DashboardWebStringId.Chart.Position': 'Position',
    'DashboardWebStringId.Chart.IsInsideOfDiagram': 'Inside diagram',
    'DashboardWebStringId.Chart.PrimaryAxisY': 'PrimaryAxisY',
    'DashboardWebStringId.Chart.SecondaryAxisY': 'SecondaryAxisY',
    'DashboardWebStringId.Chart.Name': 'Name',
    'DashboardWebStringId.Chart.PlotOnSecondaryAxis': 'Plot on secondary axis',
    'DashboardWebStringId.Chart.IgnoreEmptyPoints': 'Ignore empty points',
    'DashboardWebStringId.Chart.ShowPointMarkers': 'Show point markers',
    'DashboardWebStringId.Chart.Value': 'Value',
    'DashboardWebStringId.Chart.ShowZeroValues': 'Show for zero values',
    'DashboardWebStringId.Chart.OverlappingMode': 'Overlapping Mode',
    'DashboardWebStringId.Chart.Orientation': 'Orientation',
    'DashboardWebStringId.Chart.ShowPointLablels': 'Show Point Labels',
    'DashboardWebStringId.Chart.TooltipContent': 'Tooltips',
    'DashboardWebStringId.Chart.LabelContent': 'Data Labels',
    'DashboardWebStringId.Card.SparklineOptions': 'Sparkline Options',
    'DashboardWebStringId.Card.SparklineVisible': 'Show Sparkline',
    'DashboardWebStringId.Chart.Position.TopLeftVertical': 'Top Left Vertical',
    'DashboardWebStringId.Chart.Position.TopLeftHorizontal': 'Top Left Horizontal',
    'DashboardWebStringId.Chart.Position.TopCenterHorizontal': 'Top Center Horizontal',
    'DashboardWebStringId.Chart.Position.TopRightVertical': 'Top Right Vertical',
    'DashboardWebStringId.Chart.Position.TopRightHorizontal': 'Top Right Horizontal',
    'DashboardWebStringId.Chart.Position.BottomLeftVertical': 'Bottom Left Vertical',
    'DashboardWebStringId.Chart.Position.BottomLeftHorizontal': 'Bottom Left Horizontal',
    'DashboardWebStringId.Chart.Position.BottomCenterHorizontal': 'Bottom Center Horizontal',
    'DashboardWebStringId.Chart.Position.BottomRightVertical': 'Bottom Right Vertical',
    'DashboardWebStringId.Chart.Position.BottomRightHorizontal': 'Bottom Right Horizontal',
    'DashboardWebStringId.Chart.Position.TopCenterVertical': 'Top Center Vertical',
    'DashboardWebStringId.Chart.Position.BottomCenterVertical': 'Bottom Center Vertical',
    'DashboardWebStringId.Chart.Indicator.DashStyle': 'Dash Style',
    'DashboardWebStringId.Chart.Indicator.DashStyle.Empty': 'Empty',
    'DashboardWebStringId.Chart.Indicator.DashStyle.Solid': 'Solid',
    'DashboardWebStringId.Chart.Indicator.DashStyle.Dash': 'Dash',
    'DashboardWebStringId.Chart.Indicator.DashStyle.Dot': 'Dot',
    'DashboardWebStringId.Chart.Indicator.DashStyle.DashDot': 'DashDot',
    'DashboardWebStringId.Chart.Indicator.DashStyle.DashDotDot': 'DashDotDot',
    'DashboardWebStringId.Chart.Indicator.Type': 'Type',
    'DashboardWebStringId.Chart.Indicator.Type.Regression': 'Regression',
    'DashboardWebStringId.Chart.Indicator.Type.Trend': 'Trend',
    'DashboardWebStringId.Chart.Indicator.LegendText': 'Legend Text',
    'DashboardWebStringId.Chart.Indicator.ShowInLegend': 'Display in legend',
    'DashboardWebStringId.Chart.Indicator.Thickness': 'Thickness',
    'DashboardWebStringId.Chart.Indicator.ValueLevel': 'Value level',
    'DashboardWebStringId.Chart.Indicator.ValueLevel.Value': 'Value',
    'DashboardWebStringId.Chart.Indicator.Visible': 'Visible',
    'DashboardWebStringId.Chart.Indicator.Color': 'Color',
    'DashboardWebStringId.Chart.Indicator.Value': 'Value',
    'DashboardWebStringId.Chart.Indicators': 'Trend Indicators',
    'DashboardWebStringId.Chart.IndicatorSettings': 'Indicator settings',
    'DashboardWebStringId.FilterItem.Type': 'Type',
    'DashboardWebStringId.FilterItem.Type.Standard': 'Standard',
    'DashboardWebStringId.FilterItem.Type.Checked': 'Checked',
    'DashboardWebStringId.FilterItem.Type.Radio': 'Radio',
    'DashboardWebStringId.FilterItem.ShowAllValue': 'Show "All" Value',
    'DashboardWebStringId.FilterItem.EnableSearch': 'Enable Search',
    'DashboardWebStringId.Gauge.Min': 'Minimum Value',
    'DashboardWebStringId.Gauge.Max': 'Maximum Value',
    'DashboardWebStringId.Gauge.ScaleOptions': 'Scale Options',
    'DashboardWebStringId.Gauge.Type': 'Gauge Type',
    'DashboardWebStringId.Gauge.Type.FullQuarterGauge': 'Full Quarter Gauge',
    'DashboardWebStringId.Gauge.Type.HalfCircular': 'Half Circular',
    'DashboardWebStringId.Gauge.Type.LeftQuarterCircular': 'Left - Quarter Circular',
    'DashboardWebStringId.Gauge.Type.RightQuarterCircular': 'Right - Quarter Circular',
    'DashboardWebStringId.Gauge.Type.ThreeFourthCircular': 'Three-Quarter Circular',
    'DashboardWebStringId.Gauge.Type.LinearHorizontal': 'Linear Horizontal',
    'DashboardWebStringId.Gauge.Type.LinearVertical': 'Linear Vertical',
    'DashboardWebStringId.Gauge.Captions': 'Show Gauge Captions',
    'DashboardWebStringId.Gauge.Scale.Custom': 'Custom',
    'DashboardWebStringId.Gauge.Scale.Auto': 'Auto',
    'DashboardWebStringId.Grid.ColumnType': 'Column Type',
    'DashboardWebStringId.Grid.DisplayMode': 'Display Mode',
    'DashboardWebStringId.Grid.DisplayMode.Value': 'Value',
    'DashboardWebStringId.Grid.DisplayMode.Bar': 'Bar',
    'DashboardWebStringId.Grid.DisplayMode.Text': 'Text',
    'DashboardWebStringId.Grid.DisplayMode.Image': 'Image',
    'DashboardWebStringId.Grid.FixedWidth': 'Fixed Width',
    'DashboardWebStringId.Grid.WidthType': 'Width Type',
    'DashboardWebStringId.Grid.WidthType.Weight': 'Weight',
    'DashboardWebStringId.Grid.WidthType.FitToContent': 'Fit To Content',
    'DashboardWebStringId.Grid.DeltaOptions': 'Delta Options',
    'DashboardWebStringId.Grid.ShowStartEndValues': 'Show Start/ End Values',
    'DashboardWebStringId.Grid.TotalType': 'Total Type',
    'DashboardWebStringId.Grid.Dimension': 'Dimension',
    'DashboardWebStringId.Grid.Measure': 'Measure',
    'DashboardWebStringId.Grid.Delta': 'Delta',
    'DashboardWebStringId.Grid.Sparkline': 'Sparkline',
    'DashboardWebStringId.Grid.Hyperlink': 'Hyperlink',
    'DashboardWebStringId.Grid.Options': 'Grid Options',
    'DashboardWebStringId.Map.ShapeFileLoaded': '(SHP File)',
    'DashboardWebStringId.Map.AttributeFileLoaded': '(DBF File)',
    'DashboardWebStringId.Map.ValueMap': 'Value Map',
    'DashboardWebStringId.Map.DeltaMap': 'Delta Map',
    'DashboardWebStringId.Map.TooltipCaption': 'Tooltip Caption',
    'DashboardWebStringId.Map.TooltipActualValueCaption': 'Tooltip Actual Value Caption',
    'DashboardWebStringId.Map.TooltipDeltaCaption': 'Tooltip Delta Caption',
    'DashboardWebStringId.Map.CustomMapOptions': 'Custom Map Options',
    'DashboardWebStringId.Map.CustomMapOptionsSource': 'Source',
    'DashboardWebStringId.Map.CustomMapOptionsUrl': 'Url',
    'DashboardWebStringId.Map.CustomMapOptionsFile': 'File',
    'DashboardWebStringId.Map.CustomMapUrl': 'Shape File Url',
    'DashboardWebStringId.Map.CustomMapFile': 'Shape File',
    'DashboardWebStringId.Map.CustomAttributeFile': 'Attribute File',
    'DashboardWebStringId.Map.AttributeDimension': 'Attribute Dimension',
    'DashboardWebStringId.Map.IncludeSummaryValueToShapeTitle': 'Include Summary Value',
    'DashboardWebStringId.Map.AttributeName': 'Attribute Name',
    'DashboardWebStringId.Map.TooltipAttribute': 'Tooltip Attribute',
    'DashboardWebStringId.Map.TooltipDimensions': 'Tooltip Dimensions',
    'DashboardWebStringId.Map.Area.Custom': 'Custom',
    'DashboardWebStringId.Map.Area.WorldCountries': 'World Countries',
    'DashboardWebStringId.Map.Area.Europe': 'Europe',
    'DashboardWebStringId.Map.Area.Asia': 'Asia',
    'DashboardWebStringId.Map.Area.NorthAmerica': 'North America',
    'DashboardWebStringId.Map.Area.SouthAmerica': 'South America',
    'DashboardWebStringId.Map.Area.Africa': 'Africa',
    'DashboardWebStringId.Map.Area.USA': 'USA',
    'DashboardWebStringId.Map.Area.Canada': 'Canada',
    'DashboardWebStringId.Map.Viewport': 'Viewport',
    'DashboardWebStringId.Map.ShapeTitleAttribute': 'Shape Title Attribute',
    'DashboardWebStringId.Map.UseBindingAttribute': 'Use binding attribute',
    'DashboardWebStringId.Map.WeightedLegendType': 'Type',
    'DashboardWebStringId.Map.WeightedLegendType.Linear': 'Linear',
    'DashboardWebStringId.Map.WeightedLegendType.Nested': 'Nested',
    'DashboardWebStringId.Map.Orientation.Vertical': 'Vertical',
    'DashboardWebStringId.Map.Orientation.Horizontal': 'Horizontal',
    'DashboardWebStringId.Map.Position.TopLeft': 'Top Left',
    'DashboardWebStringId.Map.Position.TopCenter': 'Top Center',
    'DashboardWebStringId.Map.Position.TopRight': 'Top Right',
    'DashboardWebStringId.Map.Position.BottomLeft': 'Bottom Left',
    'DashboardWebStringId.Map.Position.BottomCenter': 'Bottom Center',
    'DashboardWebStringId.Map.Position.BottomRight': 'Bottom Right',
    'DashboardWebStringId.Map.Viewport.TopLatitude': 'Top Latitude',
    'DashboardWebStringId.Map.Viewport.LeftLongitude': 'Left Longitude',
    'DashboardWebStringId.Map.Viewport.RightLongitude': 'Right Longitude',
    'DashboardWebStringId.Map.Viewport.BottomLatitude': 'Bottom Latitude',
    'DashboardWebStringId.Map.Viewport.CenterPointLatitude': 'Center Point Latitude',
    'DashboardWebStringId.Map.Viewport.CenterPointLongitude': 'Center Point Longitude',
    'DashboardWebStringId.Map.Viewport.CreateViewerPaddings': 'Create Viewer Paddings',
    'DashboardWebStringId.Map.ShowLegend': 'Show Color Legend',
    'DashboardWebStringId.Map.ShowWeightedLegend': 'Show Weighted Legend',
    'DashboardWebStringId.Pie.Type': 'Pie Type',
    'DashboardWebStringId.Pie': 'Pie',
    'DashboardWebStringId.Pie.Donut': 'Donut',
    'DashboardWebStringId.Pie.LabelContentType.None': 'None',
    'DashboardWebStringId.Pie.LabelContentType.Argument': 'Argument',
    'DashboardWebStringId.Pie.LabelContentType.Percent': 'Percent',
    'DashboardWebStringId.Pie.LabelContentType.ArgumentAndPercent': 'Argument And Percent',
    'DashboardWebStringId.Pie.LabelContentType.Value': 'Value',
    'DashboardWebStringId.Pie.LabelContentType.ArgumentAndValue': 'Argument And Value',
    'DashboardWebStringId.Pie.LabelContentType.ValueAndPercent': 'Value And Percent',
    'DashboardWebStringId.Pie.LabelContentType.ArgumentValueAndPercent': 'Argument, Value And Percent',
    'DashboardWebStringId.Image.BindingMode': 'Binding Mode',
    'DashboardWebStringId.Image.BindingMode.BinaryArray': 'Binary Array',
    'DashboardWebStringId.Image.BindingMode.Uri': 'Uri',
    'DashboardWebStringId.RangeFilter.UriPattern': 'Uri Pattern',
    'DashboardWebStringId.Image.SizeMode.Clip': 'Clip',
    'DashboardWebStringId.Image.SizeMode.Stretch': 'Stretch',
    'DashboardWebStringId.Image.SizeMode.Zoom': 'Zoom',
    'DashboardWebStringId.Image.SizeMode.Squeeze': 'Squeeze',
    'DashboardWebStringId.Image.HorizontalAlignment': 'Horizontal Alignment',
    'DashboardWebStringId.Image.VerticalAlignment': 'Vertical Alignment',
    'DashboardWebStringId.Image.Alignment.Left': 'Left',
    'DashboardWebStringId.Image.Alignment.Center': 'Center',
    'DashboardWebStringId.Image.Alignment.Right': 'Right',
    'DashboardWebStringId.Image.Alignment.Top': 'Top',
    'DashboardWebStringId.Image.Alignment.Bottom': 'Bottom',
    'DashboardWebStringId.SeriesItem.SeriesDimensions': 'Series Dimensions',
    'DashboardWebStringId.TextBox.Text': 'Text',
    'DashboardWebStringId.Chart.Orientation.Default': 'Default',
    'DashboardWebStringId.Chart.Orientation.RotateRight': 'Rotate Right',
    'DashboardWebStringId.Chart.Orientation.RotateLeft': 'Rotate Left',
    'DashboardWebStringId.Chart.OverlappingMode.Hide': 'Hide',
    'DashboardWebStringId.Chart.OverlappingMode.None': 'None',
    'DashboardWebStringId.Chart.Position.Outside': 'Outside',
    'DashboardWebStringId.Chart.Position.Inside': 'Inside',
    'DashboardWebStringId.Chart.SeriesType.Weighted': 'Weighted',
    'DashboardWebStringId.Coloring.MeasuresColoringMode': 'Measures Coloring',
    'DashboardWebStringId.Coloring.Global': 'Global',
    'DashboardWebStringId.Coloring.Local': 'Local',
    'DashboardWebStringId.Delta.ValueType': 'Value Type',
    'DashboardWebStringId.Delta.ResultIndication': 'Result Indication',
    'DashboardWebStringId.Delta.ThresholdType': 'Threshold Type',
    'DashboardWebStringId.Delta.ThresholdValue': 'Threshold Value',
    'DashboardWebStringId.Sparkline.ViewType': 'View Type',
    'DashboardWebStringId.Sparkline.HighlightMinMaxPoints': 'Highlight Min/ Max Points',
    'DashboardWebStringId.Sparkline.HighlightStartEndPoints': 'Highlight Start/ End Points',
    'DashboardWebStringId.Title.Aligment.Left': 'Left',
    'DashboardWebStringId.Title.Aligment.Center': 'Center',
    'DashboardWebStringId.Parameters.LookUpSettingsType.NoLookUp': 'No Look-Up',
    'DashboardWebStringId.Parameters.LookUpSettingsType.StaticList': 'Static List',
    'DashboardWebStringId.Parameters.LookUpSettingsType.DynamicList': 'Dynamic List',
    'DashboardWebStringId.Parameters.LookUpSettingsType': 'Look-up Settings',
    'DashboardWebStringId.Parameters.DefaultValue': 'Default Value',
    'DashboardWebStringId.DataItem': 'Data Item',
    'DashboardWebStringId.DataItem.Precision': 'Precision',
    'DashboardWebStringId.DataItem.IncludeGroupSeparator': 'Include Group Separator',
    'DashboardWebStringId.DataItem.ShowValues': 'Show Values',
    'DashboardWebStringId.DataItem.ShowTotals': 'Show Totals',
    'DashboardWebStringId.DataItem.ShowGrandTotals': 'Show Grand Totals',
    'DashboardWebStringId.Dialog.ConfirmSaving': 'Confirm saving',
    'DashboardWebStringId.Dialog.Warning': 'Warning',
    'DashboardWebStringId.Dimension.GroupInterval': 'Group Interval',
    'DashboardWebStringId.Dimension.SortMode': 'Sort Mode',
    'DashboardWebStringId.Dialog.ArgumentMode': 'Axis Type',
    'DashboardWebStringId.Calculations': 'Calculations',
    'DashboardWebStringId.Calculations.Expression': 'Calculation',
    'DashboardWebStringId.Calculations.DifferenceCalculation': 'Difference of',
    'DashboardWebStringId.Calculations.Moving': 'Moving of',
    'DashboardWebStringId.Calculations.RunningTotal': 'Running Total of',
    'DashboardWebStringId.Calculations.PercentOfTotal': 'Percent Of Total of',
    'DashboardWebStringId.Calculations.Rank': 'Rank of',
    'DashboardWebStringId.Calculations.DefinitionMode': 'Definition Mode',
    'DashboardWebStringId.Calculations.WindowDefinition': 'Window Definition',
    'DashboardWebStringId.Calculations.WindowDefinitionModeSpecific': 'Specific',
    'DashboardWebStringId.Calculations.WindowDefinitionModePredefined': 'Predefined',
    'DashboardWebStringId.Calculations.DifferenceType': 'Difference Type',
    'DashboardWebStringId.TreemapLayoutAlgorithm': 'Layout Algorithm',
    'DashboardWebStringId.TilesLabelContentType': 'Tile Label',
    'DashboardWebStringId.TilesTooltipContentType': 'Tile Tooltip',
    'DashboardWebStringId.GroupsLabelContentType': 'Group Label',
    'DashboardWebStringId.GroupsTooltipContentType': 'Group Tooltip',
    'DashboardWebStringId.ShowOthersValue': 'Show "Others" Value',
    'DashboardWebStringId.TopNEnabled': 'Enabled',
    'DashboardWebStringId.Parameters.StaticListLookUpSettings': 'Static List LookUp Settings',
    'DashboardWebStringId.Parameters.DynamicListLookUpSettings': 'Dynamic List LookUp Settings',
    'DashboardWebStringId.ConvertTo': 'Convert To',
    'DashboardWebStringId.Duplicate': 'Duplicate current item',
    'DashboardWebStringId.DataSourceWizard': 'Dashboard Data Source Wizard',
    'DashboardWebStringId.RangeFilter.CustomPeriods': 'Custom Periods',
    'DashboardWebStringId.RangeFilter.StartMode': 'Start Mode',
    'DashboardWebStringId.RangeFilter.EndMode': 'End Mode',
    'DashboardWebStringId.RangeFilter.StartIntervalAndOffset': 'Start Interval and Offset',
    'DashboardWebStringId.RangeFilter.EndIntervalAndOffset': 'End Interval and Offset',
    'DashboardWebStringId.RangeFilter.StartDate': 'Start Date',
    'DashboardWebStringId.RangeFilter.EndDate': 'End Date',
    'DashboardWebStringId.RangeFilter.None': 'None',
    'DashboardWebStringId.RangeFilter.Fixed': 'Fixed',
    'DashboardWebStringId.RangeFilter.Flow': 'Flow',
    'DashboardWebStringId.Tabs.Binding': 'Binding',
    'DashboardWebStringId.Tabs.DataShaping': 'Data Shaping',
    'DashboardWebStringId.Tabs.Format': 'Format',
    'DashboardWebStringId.Calculations.Types.Moving': 'Moving',
    'DashboardWebStringId.Parameters.SortByMember': 'Sort By',
    'DashboardWebStringId.Parameters.LookUpSettingsTypeCaption': 'Look-Up Settings Type',
    'DashboardWebStringId.Dashboard.Items': 'Items',
    'DashboardWebStringId.Dashboard.Groups': 'Groups',
    'DashboardWebStringId.Dashboard.LayoutItems': 'Layout Items',
    'DashboardWebStringId.Dashboard.ComponentName': 'Component Name',
    'DashboardWebStringId.ButtonConfirm': 'Confirm',
    'DashboardWebStringId.Colorization.GroupInterval': 'Group Interval',
    'DashboardWebStringId.ButtonOn': 'ON',
    'DashboardWebStringId.ButtonOff': 'OFF',
    'DashboardWebStringId.ButtonYes': 'YES',
    'DashboardWebStringId.ButtonNo': 'NO',
    'DashboardWebStringId.ButtonDiscrete': 'DISCRETE',
    'DashboardWebStringId.ButtonContinuous': 'CONTINUOUS',
    'DashboardWebStringId.ButtonVisible': 'VISIBLE',
    'DashboardWebStringId.ButtonHidden': 'HIDDEN',
    'DashboardWebStringId.Calculations.PreviousValuesCount': 'Start Offset',
    'DashboardWebStringId.Calculations.NextValuesCount': 'End Offset',
    'DashboardWebStringId.Calculations.RankType': 'Rank Type',
    'DashboardWebStringId.Calculations.RankOrder': 'Rank Order',
    'DashboardWebStringId.TileOptions': 'Tile Options',
    'DashboardWebStringId.MapLockNavigationLocked': 'Locked',
    'DashboardWebStringId.MapLockNavigationUnlocked': 'Unlocked',
    'DashboardWebStringId.ConditionalFormatting.BarOptions': 'Bar Options',
    'DashboardWebStringId.ConditionalFormatting.CalculatedBy': 'Calculated by',
    'DashboardWebStringId.ConditionalFormatting.IconType': 'Icon Type',
    'DashboardStringId.FormatRulePredefinedColor': 'Predefined Color',
    'DashboardStringId.FormatRuleCustomColor': 'Custom Color',
    'DashboardWebStringId.FormatConditionPredefinedAppearanceCaption': 'Predefined Appearance',
    'DashboardWebStringId.FormatConditionCustomAppearanceCaption': 'Custom Appearance',
    'DashboardWebStringId.FormatConditionRangeSetNoStyleCaption': 'No Style',
    'DashboardWebStringId.EditorAutomaticValue': '(Automatic)',
    'DashboardWebStringId.ConditionalFormatting.Save': 'Save',
    'DashboardWebStringId.ConditionalFormatting.AppearanceType': 'Appearance Type',
    'DashboardWebStringId.ConditionalFormatting.FontStyle': 'Font Style',
    'DashboardWebStringId.ConditionalFormatting.FontFamily': 'Font Family',
    'DashboardWebStringId.ConditionalFormatting.FontRegular': 'Regular',
    'DashboardWebStringId.ConditionalFormatting.CustomStyleSettings': 'Custom Style Settings',
    'DashboardWebStringId.ConditionalFormatting.IconType.None': 'None',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalGreenArrowUp': 'Directional Green Arrow Up',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalYellowUpInclineArrow': 'Directional Yellow Up Incline Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalYellowSideArrow': 'Directional Yellow Side Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalYellowDownInclineArrow': 'Directional Yellow Down Incline Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalRedDownArrow': 'Directional Red Down Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalGrayArrowUp': 'Directional Gray Arrow Up',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalGrayUpInclineArrow': 'Directional Gray Up Incline Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalGraySideArrow': 'Directional Gray Side Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalGrayDownInclineArrow': 'Directional Gray Down Incline Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalGrayDownArrow': 'Directional Gray Down Arrow',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalYellowDash': 'Directional Yellow Dash',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalRedTriangleDown': 'Directional Red Triangle Down',
    'DashboardWebStringId.ConditionalFormatting.IconType.DirectionalGreenTriangleUp': 'Directional Green Triangle Up',
    'DashboardWebStringId.ConditionalFormatting.IconType.RatingFullGrayStar': 'Rating Full Gray Star',
    'DashboardWebStringId.ConditionalFormatting.IconType.RatingHalfGrayStar': 'Rating Half Gray Star',
    'DashboardWebStringId.ConditionalFormatting.IconType.RatingEmptyGrayStar': 'Rating Empty Gray Star',
    'DashboardWebStringId.ConditionalFormatting.IconType.RatingFullGrayCircle': 'Rating Full Gray Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating1QuarterGrayCircle': 'Rating 1 Quarter Gray Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating2QuartersGrayCircle': 'Rating 2 Quarters Gray Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating3QuartersGrayCircle': 'Rating 3 Quarters Gray Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.RatingEmptyGrayCircle': 'Rating Empty Gray Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating4Bars': 'Rating 4 Bars',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating3Bars': 'Rating 3 Bars',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating2Bars': 'Rating 2 Bars',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating1Bar': 'Rating 1 Bar',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating0Bars': 'Rating 0 Bars',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating4FilledBoxes': 'Rating 4 Filled Boxes',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating3FilledBoxes': 'Rating 3 Filled Boxes',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating2FilledBoxes': 'Rating 2 Filled Boxes',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating1FilledBox': 'Rating 1 Filled Box',
    'DashboardWebStringId.ConditionalFormatting.IconType.Rating0FilledBoxes': 'Rating 0 Filled Boxes',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeGreenTrafficLight': 'Shape Green Traffic Light',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeYellowTrafficLight': 'Shape Yellow Traffic Light',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeRedTrafficLight': 'Shape Red Traffic Light',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeGreenCircle': 'Shape Green Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeYellowCircle': 'Shape Yellow Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeRedCircle': 'Shape Red Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeLightRedCircle': 'Shape Light Red Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeLightGrayCircle': 'Shape Light Gray Circle',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeYellowTriangle': 'Shape Yellow Triangle',
    'DashboardWebStringId.ConditionalFormatting.IconType.ShapeRedDiamond': 'Shape Red Diamond',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorGreenCheck': 'Indicator Green Check',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorYellowExclamation': 'Indicator Yellow Exclamation',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorRedCross': 'Indicator Red Cross',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorCircledGreenCheck': 'Indicator Circled Green Check',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorCircledYellowExclamation': 'Indicator Circled Yellow Exclamation',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorCircledRedCross': 'Indicator Circled Red Cross',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorGreenFlag': 'Indicator Green Flag',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorYellowFlag': 'Indicator Yellow Flag',
    'DashboardWebStringId.ConditionalFormatting.IconType.IndicatorRedFlag': 'Indicator Red Flag',
    'DashboardWebStringId.ConditionalFormatting.IntersectionColumnDimension': 'Intersection Column Dimension',
    'DashboardWebStringId.ConditionalFormatting.IntersectionRowDimension': 'Intersection Row Dimension',
    'DashboardWebStringId.ConditionalFormatting.IntersectionLevelMode': 'Intersection Level Mode',
    'DashboardWebStringId.ConditionalFormatting.GradientRangeBar': 'Gradient Range Bar',
    'DashboardWebStringId.ConditionalFormatting.RangeGradient': 'Range Gradient',
    'DashboardWebStringId.ConditionalFormatting.SegmentNumber': 'Segment Number',
    'DashboardWebStringId.ConditionalFormatting.ValueComparison': 'Value Comparison',
    'DashboardWebStringId.ConditionalFormatting.Ranges': 'Ranges',
    'DashboardWebStringId.ConditionalFormatting.AverageType': 'Average Type',
    'DashboardWebStringId.ConditionalFormatting.Above': 'Above',
    'DashboardWebStringId.ConditionalFormatting.AboveOrEqual': 'Above or Equal',
    'DashboardWebStringId.ConditionalFormatting.Below': 'Below',
    'DashboardWebStringId.ConditionalFormatting.BelowOrEqual': 'Below or Equal',
    'DashboardWebStringId.ConditionalFormatting.StyleSettingsType': 'Style Settings Type',
    'DashboardWebStringId.ConditionalFormatting.Positive': 'Positive',
    'DashboardWebStringId.ConditionalFormatting.Negative': 'Negative',
    'DashboardWebStringId.ConditionalFormatting.MinimumType': 'Minimum Type',
    'DashboardWebStringId.ConditionalFormatting.MaximumType': 'Maximum Type',
    'DashboardWebStringId.ConditionalFormatting.DateType': 'Date Type',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringNone': 'None',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringEmpty': 'Empty',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringYesterday': 'Yesterday',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringToday': 'Today',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringTomorrow': 'Tomorrow',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringLastWeek': 'Last Week',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringThisWeek': 'This Week',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringNextWeek': 'Next Week',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAgo1': 'Last Month',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringThisMonth': 'This Month',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAfter1': 'Next Month',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringEarlierThisWeek': 'This week, prior to yesterday',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringEarlierThisMonth': 'This month, prior to previous week',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringEarlierThisYear': 'This year, prior to this month',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAgo2': 'During the month 2 months ago',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAgo3': 'During the month 3 months ago',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAgo4': 'During the month 4 months ago',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAgo5': 'During the month 5 months ago',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAgo6': 'During the month 6 months ago',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringEarlier': 'Prior to the month 6 months ago',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringPriorThisYear': 'Prior to this year',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringLaterThisWeek': 'This week, beyond tomorrow',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringLaterThisMonth': 'This month, beyond next week',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringLaterThisYear': 'This year, beyond this month',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringMonthAfter2': 'During the month in 2 months time',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringBeyond': 'Following the month in 2 months time',
    'DashboardWebStringId.ConditionalFormatting.DatesOccurringBeyondThisYear': 'Following this year',
    'DashboardWebStringId.ConditionalFormatting.CardRuleTypeDataItem': 'Data Item',
    'DashboardWebStringId.ConditionalFormatting.CardRuleTypeCard': 'Card',
    'DashboardWebStringId.ConditionalFormatting.InvalidApplyToValue': 'Please select a valid data item',
    'DashboardWebStringId.Colorization.NoSelectedSchema': 'Select color signature to edit schema',
    'DashboardWebStringId.Colorization.ColorSchemes': 'Color Schemes',
    'DashboardWebStringId.LayoutHasBeenChangedDialogMessage': 'The dashboard layout has been changed.',
    'DashboardWebStringId.SaveConfirmationDialogMessage': 'If you leave before saving, your changes will be lost.',
    'DashboardWebStringId.SaveChangesDialogMessage': 'Do you want to save changes?',
    'DashboardWebStringId.Dialog.Save': 'Save',
    'DashboardWebStringId.Dialog.DoNotSave': "Don't Save",
    'DashboardWebStringId.Dialog.UnsupportedCalculatedField': 'This data item does not support the calculated field you created. This field is available on the Data Sources page of the dashboard menu.',
    'DashboardWebStringId.Colorization.ColorSchemeType': 'Color Scheme Type',
    'DashboardWebStringId.Colorization.MeasureNames': 'Measure Names',
    'DashboardWebStringId.Colorization.AddColor': 'Add Color',
    'DashboardWebStringId.Colorization.ValidationSelectMeasure': 'Select at least one measure',
    'DashboardWebStringId.ColoringModeAuto': 'Auto',
    'DashboardWebStringId.ColoringModeOff': 'Off',
    'DashboardWebStringId.ColoringModeOn': 'On',
    'DashboardWebStringId.ColorScheme.Empty': 'The current dashboard does not contain color schemes',
    'DashboardWebStringId.DashboardItemMenu.Bindings.SelectDataSourceButton': 'Select a data source',
    'DashboardWebStringId.DashboardItemMenu.Bindings.SelectDataSourceButtonTooltip': 'To add a data source to the current dashboard, go to Menu | Data Sources.',
    'DashboardWebStringId.DashboardItemMenu.Bindings.ChangedDataSourceMessage': 'The data source for the {0} item was changed to {1}',
    'DashboardWebStringId.ConditionalFormatting.Miscellaneous': 'Miscellaneous',
    'DashboardWebStringId.Dashboards': 'Dashboards',
    'DashboardWebStringId.EditInDesigner': 'Edit in Designer',
    'DashboardWebStringId.NoDashboards': 'There are no dashboards to display.',
    'DashboardWebStringId.AddNewDashboard': 'To add a new dashboard, click',
    'DashboardWebStringId.NewDashboard': 'New Dashboard',
    'DashboardWebStringId.Binding.Searching': 'Start typing to search...',
    'DashboardWebStringId.Binding.EmptySearchResult': 'Nothing was found',
    'DashboardWebStringId.Errors.AttemptToLoadData': 'An error occurred during an attempt to load data',
    'DashboardWebStringId.ConditionType': 'Condition Type',
    'DashboardWebStringId.ConditionalFormatting.UnsupportedDataType': 'This data type is not supported',
    'DashboardWebStringId.FormatRule': 'Format Rule',
    'DashboardWebStringId.Notification.DashboardSaving': 'Dashboard is saving',
    'DashboardWebStringId.Notification.DashboardSaved': 'Dashboard is saved',
    'DashboardWebStringId.Notification.DashboardCanNotBeSaved': 'The dashboard cannot be saved',
    'DashboardWebStringId.Notification.DashboardSavingIsNotAvailable': 'Saving is not available',
    'DashboardWebStringId.Notification.AttemptToLoadDashboards': 'An error occurred during an attempt to load the list of available dashboards',
    'DashboardWebStringId.Notification.DashboardLoading': 'Dashboard is loading...',
    'DashboardWebStringId.Notification.AttemptToLoadDashboard': 'An error occurred during an attempt to load the dashboard ',
    'DashboardWebStringId.Notification.AttemptToConnectionStrings': 'An error occurred during an attempt to load connection strings',
    'DashboardWebStringId.Notification.MethodIsNotAllowedInViewerMode': 'This method is not allowed in the viewer mode',
    'DashboardWebStringId.Notification.AttemptToLoadParameterValues': 'An error occurred during an attempt to load parameter values for the data source ',
    'DashboardWebStringId.Notification.AttemptToGetUnderlyingData': 'An error occurred during an attempt to get underlying data for the item ',
    'DashboardWebStringId.Notification.AttemptToConvertItemToType': 'An error occurred during an attempt to convert the item to the type ',
    'DashboardWebStringId.Notification.AttemptToLoadDimensionUniqueValues': 'An error occurred during an attempt to load dimension unique values for the data source ',
    'DashboardWebStringId.Notification.AttemptToLoadFieldListForDataSource': 'An error occurred during an attempt to load the field list for the data source ',
    'DashboardWebStringId.Notification.AttemptToGetColorScheme': 'An error occurred during an attempt to get the color scheme',
    'DashboardWebStringId.Notification.AttemptToLoadAvailableFontFamilies': 'An error occurred during an attempt to load available font families',
    'DashboardWebStringId.Notification.AttemptToPerformExport': 'An error occurred during an attempt to perform export',
    'DashboardWebStringId.Notification.NewDashboardCannotBeCreated': 'A new dashboard cannot be created',
    'DashboardWebStringId.Notification.ErrorHasOccuredOn': 'An error occurred during an',
    'DashboardWebStringId.Notification.UnsupportedDataSourceType': 'Unsupported data source type',
    'DashboardWebStringId.Notification.QueryDoesNotExist': 'The query with the specified name does not exist: {0}.',
    'DashboardWebStringId.Notification.UnderlyingRequestDataNotLoaded': 'The data for the \'{0}\' dashboard item has not been loaded yet. Please check whether the \'getItemData\' method call returns an initialized non-null value before you request the underlying data.',
    'DashboardWebStringId.LimitVisibleData.DisplayAllDataTooltip': 'This dashboard item is bound to a large dataset and does not currently display all data to retain performance. Click this button to display all data.',
    'DashboardWebStringId.LimitVisibleData.IncreasePerformanceTooltip': 'This dashboard item is bound to a large data set. To increase performance, click this icon to limit visible data.',
    'DashboardWebStringId.ConditionalFormatting.Condition': 'Condition',
    'DashboardWebStringId.CardLayout.MinWidth': 'Min Width',
    'DashboardWebStringId.CardLayout.MaxWidth': 'Max Width',
    'DashboardWebStringId.CardLayout.Editor.CardLayout': 'Card Layout',
    'DashboardWebStringId.CardLayout.Editor.FormatOptions': 'Format Options',
    'DashboardWebStringId.CardLayout.LayoutOptions': 'Layout Options',
    'DashboardWebStringId.CardLayout.ResetTemplate': 'Reset',
    'DashboardWebStringId.CardLayout.ApplyToAllCards': 'Apply to all Cards',
    'DashboardWebStringId.Binding.ValuesPattern': 'Values {0}',
    'DashboardWebStringId.Parameters.ParameterNameShouldBeUnique': 'A name of the parameter should be unique',
    'DashboardWebStringId.Title.ImageEmbedded': 'Embedded',
    'DashboardWebStringId.Title.ImageLinked': 'Linked',
    'DashboardWebStringId.Title.ImageNone': 'None',
    'DashboardWebStringId.RangeFilter.SetDefaultCustomPeriod': 'Set as Default',
    'DashboardWebStringId.RangeFilter.ClearDefaultCustomPeriod': 'Clear Default Custom Period',
    'DashboardWebStringId.MobileLayout.Back': 'Back',
    'DashboardWebStringId.MobileLayout.SwitchToViewer': 'You have opened the Web Dashboard in the Designer mode. The control supports a mobile layout only if it works in the Viewer mode. Do you want to switch to the Viewer mode?',
    'DashboardWebStringId.Search': 'Search...',
    'DashboardWebStringId.SearchFontFamilies': 'Search font families...',
    'DashboardStringId.DefaultNameTabPage': 'Page',
    'DashboardStringId.DefaultNameChartPane': 'Pane',
    'DashboardStringId.DefaultNameChartIndicator': 'indicator',
    'DashboardStringId.NewCalculatedFieldNamePrefix': 'Calculated Field',
    'DashboardStringId.NewParameterNamePrefix': 'Parameter',
    'AnalyticsCoreStringId.Parameter_Type_String': 'String',
    'AnalyticsCoreStringId.Parameter_Type_DateTime': 'Date',
    'AnalyticsCoreStringId.Parameter_Type_Int16': 'Number (16 bit integer)',
    'AnalyticsCoreStringId.Parameter_Type_Int32': 'Number (32 bit integer)',
    'AnalyticsCoreStringId.Parameter_Type_Int64': 'Number (64 bit integer)',
    'AnalyticsCoreStringId.Parameter_Type_Float': 'Number (floating-point)',
    'AnalyticsCoreStringId.Parameter_Type_Double': 'Number (double-precision floating-point)',
    'AnalyticsCoreStringId.Parameter_Type_Decimal': 'Number (decimal)',
    'AnalyticsCoreStringId.Parameter_Type_Boolean': 'Boolean',
    'AnalyticsCoreStringId.Parameter_Type_Guid': 'Guid',
    'AnalyticsCoreStringId.ExpressionEditor_ExpressionCaption': 'Expression Editor',
    'DataAccessUIStringId.FiltersView': 'Filter Editor',
    'DashboardStringId.SeriesTypeGroupArea': 'Area',
    'DashboardStringId.SeriesTypeGroupBar': 'Bar',
    'DashboardStringId.SeriesTypeGroupBubble': 'Bubble',
    'DashboardStringId.SeriesTypeGroupFinancial': 'Financial',
    'DashboardStringId.SeriesTypeGroupPointLine': 'Point / Line',
    'DashboardStringId.SeriesTypeGroupRange': 'Range',
    'DashboardStringId.FunctionCategoryWindow': 'Window',
    'DashboardStringId.FunctionCategoryAggregate': 'Aggregate',
    'DashboardStringId.FunctionCategoryAdvanced': 'Advanced',
    'DashboardStringId.FunctionDescriptionRunningCustomAggregate': 'RunningAggregate(SummaryExpression, CustomAggregateName) - Returns the cumulative result of the custom function applied to specified expression from the first row in the window to the current row.\r\n',
    'DashboardStringId.FunctionDescriptionWindowCustomAggregate': 'WindowAggregate(SummaryExpression, StartOffset, EndOffset, CustomAggregateName) - Returns the result of the custom function applied to the specified expression in the window.\r\n',
    'DashboardStringId.FunctionDescriptionWindowAvg': 'WindowAvg(SummaryExpression, StartOffset, EndOffset)\r\nReturns the average of the expression within the window, which is defined using offsets from the current row.\r\n  ',
    'DashboardStringId.FunctionDescriptionRunningMin': 'RunningMin(SummaryExpression)\r\nReturns the running minimum of the specified expression from the first row in the window to the current row.\r\n  ',
    'DashboardStringId.FunctionDescriptionTotal': 'Total(SummaryExpression)\r\nReturns the total for the specified expression in a table calculation window.\r\n  ',
    'DashboardStringId.FunctionDescriptionRankCompetition': 'RankCompetition(SummaryExpression, ["asc", "desc"])\r\nReturns the standard competition rank for the current row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionLookup': 'Lookup(SummaryExpression, Position)\r\nReturns the value of the expression in a target position specified as a relative offset from the current position.\r\n  ',
    'DashboardStringId.FunctionDescriptionRunningCount': 'RunningCount(SummaryExpression)\r\nReturns the running count of the specified expression from the first row in the window to the current row.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowMax': 'WindowMax(SummaryExpression, StartOffset, EndOffset)\r\nReturns the maximum of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionVarp': 'Varp(Value)\r\nReturns the variance of a population, where the population is the entire data to be summarized.',
    'DashboardStringId.FunctionDescriptionRunningAvg': 'RunningAvg(SummaryExpression)\r\nReturns the running average of the specified expression from the first row in the window to the current row.\r\n  ',
    'DashboardStringId.FunctionDescriptionFirst': 'First()\r\nReturns the number of rows from the current row to the first row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowMin': 'WindowMin(SummaryExpression, StartOffset, EndOffset)\r\nReturns the minimum of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowStdDev': 'WindowStdDev(SummaryExpression, StartOffset, EndOffset)\r\nReturns the sample standard deviation of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionLast': 'Last()\r\nReturns the number of rows from the current row to the last row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowCount': 'WindowCount(SummaryExpression, StartOffset, EndOffset)\r\nReturns the count of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionCountDistinct': 'CountDistinct(Value)\r\nReturns the number of unique objects in a collection.',
    'DashboardStringId.FunctionDescriptionIndex': 'Index()\r\nReturns the index of the current row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowStdDevp': 'WindowStdDevp(SummaryExpression, StartOffset, EndOffset)\r\nReturns the biased standard deviation of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowMode': 'WindowMode(SummaryExpression, StartOffset, EndOffset)\r\n Returns the mode of the expression within the window.',
    'DashboardStringId.FunctionDescriptionRunningMax': 'RunningMax(SummaryExpression)\r\nReturns the running maximum of the specified expression from the first row in the window to the current row.\r\n  ',
    'DashboardStringId.FunctionDescriptionRankPercentile': 'RankPercentile(SummaryExpression, ["asc", "desc"])\r\nReturns the percentile rank for the current row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionRankDense': 'RankDense(SummaryExpression, ["asc", "desc"])\r\nReturns the dense rank for the current row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowSum': 'WindowSum(SummaryExpression, StartOffset, EndOffset)\r\nReturns the sum of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowCountDistinct': 'WindowCountDistinct(SummaryExpression, StartOffset, EndOffset)\r\nReturns the distinct count of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionStdDevp': 'StdDevp(Value)\r\nReturns the standard deviation of a population, where the population is the entire data to be summarized.',
    'DashboardStringId.FunctionDescriptionSize': 'Size()\r\nReturns the number of rows in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionMedian': 'Median(Value)\r\nReturns the median of the values in a collection.',
    'DashboardStringId.FunctionDescriptionRankModified': 'RankModified(SummaryExpression, ["asc", "desc"])\r\nReturns the modified competition rank for the current row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowMedian': 'WindowMedian(SummaryExpression, StartOffset, EndOffset)\r\nReturns the median of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionRunningSum': 'RunningSum(SummaryExpression)\r\nReturns the running sum of the specified expression from the first row in the window to the current row.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowVarp': 'WindowVarp(SummaryExpression, StartOffset, EndOffset)\r\nReturns the biased variance of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionWindowVar': 'WindowVar(SummaryExpression, StartOffset, EndOffset)\r\nReturns the variance of the expression within the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionStdDev': 'StdDev(Value)\r\nReturns an estimate of the standard deviation of a population, where the sample is a subset of the entire population.',
    'DashboardStringId.FunctionDescriptionRankUnique': 'RankUnique(SummaryExpression, ["asc", "desc"])\r\nReturns the unique rank for the current row in the window.\r\n  ',
    'DashboardStringId.FunctionDescriptionVar': 'Var(Value)\r\nReturns an estimate of the variance of a population, where the sample is a subset of the entire population.',
    'DashboardStringId.FunctionDescriptionAggr': 'aggr(SummaryExpression, Dimension1, Dimension2, ...)\r\nUses the detail level and summary function to aggregate underlying data. The level depends on the number of dimensions.',
    'DashboardStringId.FunctionDescriptionAggrJoinRule': "joinRule(dimension, searchValue)\r\nSpecifies the dimension for the 'aggr' function and a column whose values are used in the join operation after aggregation is completed.",
    'DashboardStringId.FunctionDescriptionWindowDefinition': 'w(WindowExpression, partitionByFunction, orderByFunction)\r\nCalculates aggregated values with the specified window function for the window defined by the specified partitioning and ordering.',
    'DashboardStringId.FunctionDescriptionWindowDefinitionPartitionBy': 'partitionBy(column1, column2, ...)\r\nSpecifies the columns by which the data rows are partitioned. The window function is applied to each partition separately.',
    'DashboardStringId.FunctionDescriptionWindowDefinitionOrderBy': 'orderBy(column1, column2, ...)\r\nSpecifies the logical order in which the window function calculation is performed on the rows in the window.',
    'DashboardStringId.FunctionDescriptionWindowDefinitionOrderAsc': 'asc(column)\r\nSpecifies that the values in the specified column are sorted in ascending order. This is the default sort order.',
    'DashboardStringId.FunctionDescriptionWindowDefinitionOrderDesc': 'desc(column)\r\nSpecifies that the values in the specified column are sorted in descending order.',
    'DashboardStringId.AggregateExFunctionDescriptionCountNotNull': 'CountNotNull(Value)\r\nReturns a number of non-null objects in a collection.',
    'DashboardStringId.AggregateExFunctionDescriptionMode': 'Mode(Value)\r\nThe mode of the values.A mode is the value that is repeated more often than any other.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetDateHour': 'GetDateHour(DateTime)\r\nExtracts the date part with the hour value from the defined DateTime.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetDateHourMinute': 'GetDateHourMinute(DateTime)\r\nExtracts the date part with the hour and minute values from the defined DateTime.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetDateHourMinuteSecond': 'GetDateHourMinuteSecond(DateTime)\r\nExtracts the date part with the hour, minute, and second values from the defined DateTime.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetDateMonthYear': 'GetDateHourMinuteSecond(DateTime)\r\nExtracts the date part with the hour, minute, and second values from the defined DateTime.',
    'DashboardStringId.FunctionDescriptionGetDateWeekYear': 'GetDateWeekYear(DateTime)\r\nReturns the date that corresponds the first day of the week in the defined DateTime depending on the culture settings.',
    'DashboardStringId.FunctionDescriptionGetDateWeekYear2': "GetDateWeekYear(DateTime, ['FirstDay', 'FirstFullWeek', 'FirstFourDayWeek'])\r\nReturns the date of the first day of the week for a given DateTime. The second parameter determines the rule for calculating the calendar week.",
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetDateQuarterYear': 'GetDateQuarterYear(DateTime)\r\nExtracts the date with the quarter and year from the defined DateTime.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetQuarter': 'GetQuarter(DateTime)\r\nExtracts a quarter from the defined DateTime.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetWeekOfMonth': 'GetWeekOfMonth(DateTime)\r\nExtracts the week of the month from the defined DateTime.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionGetWeekOfYear': 'GetWeekOfYear(DateTime)\r\nExtracts the week of the year from the defined DateTime.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionMakeDateTime3': 'MakeDateTime(Year, Month, Day)\r\nReturns a date value constructed from the specified Year, Month and Day.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionMakeDateTime4': 'MakeDateTime(Year, Month, Day, Hour)\r\nReturns a date value constructed from the specified Year, Month, Day and Hour.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionMakeDateTime5': 'MakeDateTime(Year, Month, Day, Hour, Minute)\r\nReturns a date value constructed from the specified Year, Month, Day, Hour and Minute.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionMakeDateTime6': 'MakeDateTime(Year, Month, Day, Hour, Minute, Second)\r\nReturns a date value constructed from the specified Year, Month, Day, Hour, Minute and Second.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionToBoolean': 'ToBoolean(Value)\r\nConverts Value to an equivalent Boolean value.',
    'DashboardStringId.FunctionOperatorExFunctionDescriptionToDateTime': 'ToDateTime(Value)\r\nConverts Value to a DateTime value.',
    'DashboardStringId.SparklineIndicationModeLine': 'Line',
    'DashboardStringId.SparklineIndicationModeArea': 'Area',
    'DashboardStringId.SparklineIndicationModeBar': 'Bar',
    'DashboardStringId.SparklineIndicationModeWinLoss': 'Win/Loss',
    'DashboardStringId.DefaultNameDateFilterItem': 'Date Filter',
    'DashboardWebStringId.DateFilter.FilterType': 'Filter Type',
    'DashboardWebStringId.DateFilter.FilterTypeExact': 'Exact',
    'DashboardWebStringId.DateFilter.FilterTypeAfter': 'After',
    'DashboardWebStringId.DateFilter.FilterTypeBefore': 'Before',
    'DashboardWebStringId.DateFilter.FilterTypeBetween': 'Between',
    'DashboardWebStringId.DateFilter.ArrangementMode': 'Arrangement Mode',
    'DashboardWebStringId.DateFilter.ArrangementModeAutoHeight': 'Auto Height',
    'DashboardWebStringId.DateFilter.ArrangementModeHorizontal': 'Horizontal',
    'DashboardWebStringId.DateFilter.ArrangementModeVertical': 'Vertical',
    'DashboardWebStringId.DateFilter.DatePickerLocation': 'Date Picker Location',
    'DashboardWebStringId.DateFilter.DatePickerLocationFar': 'Far',
    'DashboardWebStringId.DateFilter.DatePickerLocationNear': 'Near',
    'DashboardWebStringId.DateFilter.DatePickerLocationHidden': 'Hidden',
    'DashboardStringId.DateFilterDisplayTextPattern': 'Display Text Pattern',
    'DashboardStringId.DateFilterDatePickerButtonDefaultText': 'Set Filter...',
    'DashboardStringId.FromDatePeriodCaption': 'From {0}',
    'DashboardStringId.ToDatePeriodCaption': 'To {0}',
    'DashboardStringId.FromToDatePeriodCaption': '{0} - {1}',
    'DashboardStringId.UniqueNameValidationMessage': 'A name should be unique.',
    'DashboardStringId.NumberRangeValidationMessage': 'Value is out of ranges',
    'DashboardStringId.GridTotalAutoTemplate': '{0}',
    'DashboardStringId.GridTotalValueTemplate': '{0}',
    'DashboardStringId.GridTotalTemplate': '{0} = {1}',
    'DashboardStringId.GridTotalTypeCount': 'Count',
    'DashboardStringId.GridTotalTypeMin': 'Min',
    'DashboardStringId.GridTotalTypeMax': 'Max',
    'DashboardStringId.GridTotalTypeAvg': 'Avg',
    'DashboardStringId.GridTotalTypeSum': 'Sum',
    'DashboardStringId.GridTotalTypeAuto': 'Auto',
    'DashboardStringId.ActionShowDataInspector': 'Inspect Data',
    'DashboardStringId.InspectedData': 'Inspected Data:',
    'DashboardStringId.InspectedTypeFormatString': '({0} Data)',
    'DashboardStringId.AggregatedDataType': 'Aggregated',
    'DashboardStringId.RawDataType': 'Raw',
    'DashboardStringId.OLAPUnavailableRawData': 'OLAP Data Source - Data Unavailable',
    'DashboardStringId.SparklineValuesSeparator': ', ',
    'DashboardStringId.SparklineArgumentValueSeparator': ' - ',
    'DashboardStringId.DataInspectorButtonClose': 'Close',
    'DashboardWebStringId.Grid.FilterClause.ApplyFilter': 'Apply filter',
    'DashboardWebStringId.Grid.FilterClause.ResetOperation': 'Reset',
    'DashboardWebStringId.Grid.FilterClause.Contains': 'Contains',
    'DashboardWebStringId.Grid.FilterClause.DoesNotContain': 'Does not contain',
    'DashboardWebStringId.Grid.FilterClause.StartsWith': 'Starts with',
    'DashboardWebStringId.Grid.FilterClause.EndsWith': 'Ends with',
    'DashboardWebStringId.Grid.FilterClause.Equals': 'Equals',
    'DashboardWebStringId.Grid.FilterClause.DoesNotEqual': 'Does not equal',
    'DashboardWebStringId.Grid.FilterClause.Less': 'Less than',
    'DashboardWebStringId.Grid.FilterClause.Greater': 'Greater than',
    'DashboardWebStringId.Grid.FilterClause.LessOrEqual': 'Less than or equal to',
    'DashboardWebStringId.Grid.FilterClause.GreaterOrEqual': 'Greater than or equal to',
    'DashboardWebStringId.Grid.FilterClause.Between': 'Between',
    'DashboardWebStringId.Grid.FilterClause.BetweenStart': 'Start',
    'DashboardWebStringId.Grid.FilterClause.BetweenEnd': 'End',
    'DashboardWebStringId.AccordionTab.Content': 'Content',
    'DashboardWebStringId.TextBoxItemEditor.NoFields': 'No dashboard fields',
    'DashboardWebStringId.TextBoxItemEditor.InsertField': 'Insert Field',
    'DashboardWebStringId.TextBoxItemEditor.EditTextButton': 'Edit Text',
    'DashboardWebStringId.TextBoxItemEditor.DialogCaption': 'Text Editor',
    'DashboardWebStringId.TextBoxItemEditor.DialogSaveAndClose': 'Save and Close',
    'DashboardWebStringId.TextBoxItemEditor.DialogClose': 'Close',
    'DashboardStringId.StartDate': '(Start Date)',
    'DashboardStringId.EndDate': '(End Date)',
    'DashboardWebStringId.AccordionTab.MeasureFilter': 'Filter',
    'DashboardStringId.FunctionDescriptionAggregateFilter': 'filter(SummaryExpression, filterCriteria)\r\nFilters underlying data with specified filter criteria and aggregates filtered data using current detail level and the summary function.',
    'DashboardWebStringId.TabPages': 'Tab Pages',
    'DashboardWebStringId.More': 'More',
    'DashboardStringId.MeasureFilterCaptionFormatString': 'Filtered {0}',
    'DashboardWebStringId.AccordionTab.ItemFilter': 'Item Filter',
    'DashboardWebStringId.AccordionTab.VisibleDataFilter': 'Visible Data Filter',
    'DashboardStringId.LayoutDimensionModeAuto': 'Auto',
    'DashboardStringId.LayoutDimensionModeFixed': 'Fixed',
    'DashboardWebStringId.DesignerToolbar.Width': 'W:',
    'DashboardWebStringId.DesignerToolbar.Height': 'H:',
    'DashboardWebStringId.DesignerToolbar.Pixels': 'px',
    'DashboardWebStringId.DesignerToolbar.LayoutDimensionValidationError': 'The value should be between {0} and {1}',
    'DataAccessStringId.Wizard_NoDataSourcesForDataFederationMessage': 'Provide at least one data source to be used to create a federated data source.',
    'DevExpressWebStringId.AriaLabel.EmptyDashboardItem': 'Dashboard item is empty',
    'DevExpressWebStringId.AriaLabel.DashboardItem': 'Dashboard item, {0}',
    'DevExpressWebStringId.AriaLabel.DashboardMasterFilter': 'Master filter, {0}',
    'DevExpressWebStringId.AriaLabel.DashboardItemUpdated': '{0} updated',
    'DevExpressWebStringId.AriaLabel.DashboardTitle': 'Dashboard title, {0}'
};
export function tryLocalize(key) {
    return localize(key) || key;
}
export function getLocalizationById(key) {
    return localize(key) || key;
}
export function formatLocalizable(localizationId, arg1) {
    return format(getLocalizationById(localizationId), getLocalizationById(arg1));
}
export function getDefaultLocalization() {
    return defaultLocalization;
}
